import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Sparkles, Image as ImageIcon, Send, Copy, Loader2, Linkedin, Facebook, Instagram,
  Save, Trash2, RefreshCw, Wand2, Camera, X, Newspaper, CheckCircle2, Zap,
  Bot, AlertTriangle, ExternalLink, ArrowRight, Radar, ShieldCheck, UploadCloud, Plus,
  Calendar as CalendarIcon, CalendarDays, Clock, ChevronLeft, ChevronRight, ChevronDown,
  BarChart3, Eye, TrendingUp, Target, MessageSquare, Share2, MousePointer, Users,
  Layers,
} from 'lucide-react';

const PLATFORMS = [
  { id: 'linkedin',  label: 'LinkedIn',  icon: Linkedin,  color: 'text-[#0A66C2] bg-blue-50 border-blue-200' },
  { id: 'facebook',  label: 'Facebook',  icon: Facebook,  color: 'text-[#1877F2] bg-blue-50 border-blue-200' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-600 bg-pink-50 border-pink-200' },
];

const ASPECT_RATIOS = [
  { id: '1:1',  label: 'Carré 1:1 (Instagram)' },
  { id: '4:5',  label: 'Portrait 4:5 (Instagram)' },
  { id: '16:9', label: 'Paysage 16:9 (LinkedIn / Facebook)' },
];

const CONTENT_TYPE_LABELS = {
  realisations_chantier: 'Réalisation',
  avant_apres: 'Avant/Après',
  conseil_travaux: 'Conseil',
  pedagogique: 'Pédagogique',
  erreur_a_eviter: 'Erreur à éviter',
  budget_travaux: 'Budget',
  materiaux: 'Matériau',
  tendance_renovation: 'Tendance',
  actualite_btp: 'Actualité BTP',
  reglementation: 'Réglementation',
  coulisses: 'Coulisses',
  temoignage_client: 'Témoignage',
  presentation_entreprise: 'Entreprise',
  partenaires_artisans: 'Partenaires',
  commercial: 'Commercial',
};
const OBJECTIVE_LABELS = {
  notoriete: 'Notoriété',
  engagement: 'Engagement',
  credibilite: 'Crédibilité',
  preuve_sociale: 'Preuve sociale',
  recrutement_partenaires: 'Recrutement partenaires',
  generation_prospects: 'Prospects',
};

/**
 * Retourne l'état de complétion d'un post pour le badge calendrier.
 *  - red    : pas encore de texte (post pré-planifié à générer)
 *  - orange : texte prêt mais pas d'image
 *  - green  : texte + au moins une image = prêt à publier
 *  - blue   : déjà publié (status = published)
 */
function postCompletionStatus(post) {
  if (!post) return { color: 'slate', label: 'inconnu' };
  if (post.status === 'published' || post.published_to?.length) {
    return { color: 'blue', label: 'Publié' };
  }
  const hasText = post.variants && Object.keys(post.variants).length > 0;
  const hasImage = !!post.image_url || (post.images && Object.values(post.images).some(Boolean));
  if (!hasText) return { color: 'red',   label: 'À préparer' };
  if (!hasImage) return { color: 'orange', label: 'Texte prêt' };
  return { color: 'green', label: 'Prêt à publier' };
}
const COMPLETION_DOT = {
  red: 'bg-red-500', orange: 'bg-amber-500', green: 'bg-emerald-500', blue: 'bg-sky-500', slate: 'bg-slate-300',
};

export default function MarketingStudioAdvanced() {
  const [view, setView] = useState('agent'); // agent | compose | library | calendar
  const [brief, setBrief] = useState({
    theme: '', context: '', platforms: ['linkedin', 'facebook', 'instagram'],
    tone: 'auto', target_audience: '', call_to_action: '',
    source_name: '', source_url: '', needs_verification: false,
    content_type: null, objective: null,
    carousel: false, carousel_slides: 3, carousel_template: null,
  });
  const [variants, setVariants] = useState({});   // {platform: text}
  const [imageUrl, setImageUrl] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageRatio, setImageRatio] = useState('1:1');
  const [applyBrand, setApplyBrand] = useState(true);   // Signature Reno Resolve (logo + barre or)
  const [generatingText, setGeneratingText] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [posts, setPosts] = useState([]);
  const [carouselSlides, setCarouselSlides] = useState([]);   // [{title, body, image_prompt, image_url?}]
  const [carouselTemplateLabel, setCarouselTemplateLabel] = useState('');
  const [generatingCarouselImages, setGeneratingCarouselImages] = useState(false);
  const [carouselImageRatio, setCarouselImageRatio] = useState('1:1');
  const [carouselTemplates, setCarouselTemplates] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [agentRunning, setAgentRunning] = useState(false);
  const [validating, setValidating] = useState(false);
  // Préférences Agent (sujets prio/évités + variété visuelle)
  const [agentPrefs, setAgentPrefs] = useState({ focus_topics: [], avoid_topics: [], tone_hint: '', visual_variety: true });
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [focusInput, setFocusInput] = useState('');
  // Social Agent V2 (fondation)
  const [v2Status, setV2Status] = useState(null);
  const [v2Loading, setV2Loading] = useState(false);
  const [v2Bootstrapping, setV2Bootstrapping] = useState(false);
  // V2.1 — Brand Brain
  const [v2Intelligence, setV2Intelligence] = useState(null);
  const [v2Analyzing, setV2Analyzing] = useState(false);
  const [v2Sources, setV2Sources] = useState([]);
  // V2.2 — Strategist
  const [v2Strategy, setV2Strategy] = useState(null);
  const [v2StratLoading, setV2StratLoading] = useState(false);
  const [v2Goals, setV2Goals] = useState([
    { id: 'g1', name: 'Développer marchés collectivités', priority: 1 },
    { id: 'g2', name: 'Crédibilité technique', priority: 2 },
  ]);
  const [newGoalName, setNewGoalName] = useState('');
  const [avoidInput, setAvoidInput] = useState('');

  // V2.3 — Planner
  const [v2Plan, setV2Plan] = useState(null);
  const [v2PlanLoading, setV2PlanLoading] = useState(false);
  const [v2PlanApproving, setV2PlanApproving] = useState(false);

  // V2.4 — Content Inbox
  const [v2Inbox, setV2Inbox] = useState([]);
  const [v2InboxLoading, setV2InboxLoading] = useState(false);
  const [v2InboxUploading, setV2InboxUploading] = useState(false);
  const [v2InboxProcessingId, setV2InboxProcessingId] = useState(null);
  const [v2InboxFiles, setV2InboxFiles] = useState([]);
  const [v2InboxText, setV2InboxText] = useState('');
  const [v2ActivityText, setV2ActivityText] = useState('');
  const [v2ActivityDate, setV2ActivityDate] = useState('');
  const [v2InboxCandidates, setV2InboxCandidates] = useState([]);
  // V2.5 — Media Vision
  const [v2VisionAnalyzingId, setV2VisionAnalyzingId] = useState(null);
  // V2.6 — Copywriter (drafts par concept)
  const [v2DraftsByConcept, setV2DraftsByConcept] = useState({});
  const [v2DraftGenerating, setV2DraftGenerating] = useState(null);
  const [v2DraftActioning, setV2DraftActioning] = useState(null);
  const [v2DraftExpanded, setV2DraftExpanded] = useState(null);
  // V2.7 — Editorial Reviewer
  const [v2ReviewsByDraft, setV2ReviewsByDraft] = useState({});
  const [v2ReviewLoading, setV2ReviewLoading] = useState(null);
  const [v2ReviewActioning, setV2ReviewActioning] = useState(null);
  // V3.1 — Preview plateforme (visible) séparée de la sélection (cochée)
  //   previewPlatformByDraft : plateforme actuellement affichée en gros
  //   selectedPlatformsByDraft : miroir local des cases cochées (source
  //   canonique = draft.selected_platforms côté backend, persisté).
  const [previewPlatformByDraft, setPreviewPlatformByDraft] = useState({});
  const [selectedPlatformsByDraft, setSelectedPlatformsByDraft] = useState({});
  const [savingSelectedByDraft, setSavingSelectedByDraft] = useState({});

  // Agent autonome
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [scanSummary, setScanSummary] = useState(null);
  const [topicsHistory, setTopicsHistory] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(null);

  // Calendrier
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [calPosts, setCalPosts] = useState([]);
  const [loadingCal, setLoadingCal] = useState(false);

  // Modal aperçu post
  const [previewPost, setPreviewPost] = useState(null);

  // Insights
  const [insightsSummary, setInsightsSummary] = useState(null);
  const [insightsAnalysis, setInsightsAnalysis] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [analyzingInsights, setAnalyzingInsights] = useState(false);

  // LinkedIn
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [linkedinOrgs, setLinkedinOrgs] = useState([]);
  const [selectedOrgUrn, setSelectedOrgUrn] = useState('');

  // Meta (Facebook + Instagram)
  const [metaStatus, setMetaStatus] = useState(null);

  useEffect(() => { if (view === 'library') loadPosts(); }, [view]);
  useEffect(() => { if (view === 'agent') loadHistory(); }, [view]);
  useEffect(() => { if (view === 'calendar') loadCalendar(); }, [view, calMonth]);
  useEffect(() => { if (view === 'insights') loadInsights(); }, [view]);
  useEffect(() => { loadLinkedinStatus(); loadMetaStatus(); loadCarouselTemplates(); loadPendingCount(); loadAgentPrefs(); loadV2Status(); }, []);
  useEffect(() => {
    // Callback OAuth : le backend redirige avec ?linkedin=connected, ?meta=connected ou ?xxx_error=yyy
    const params = new URLSearchParams(window.location.search);
    if (params.get('linkedin') === 'connected') {
      toast.success('LinkedIn connecté ✓');
      loadLinkedinStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('linkedin_error')) {
      toast.error('Erreur LinkedIn : ' + params.get('linkedin_error'));
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('meta') === 'connected') {
      toast.success('Facebook & Instagram connectés ✓');
      loadMetaStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('meta_error')) {
      toast.error('Erreur Meta : ' + params.get('meta_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadPosts = async () => {
    try { const r = await api.get('/marketing/posts'); setPosts(r.data.posts || []); }
    catch (_) { setPosts([]); }
  };

  const loadHistory = async () => {
    try {
      const r = await api.get('/marketing/topics/history?limit=30');
      setTopicsHistory(r.data.topics || []);
    } catch (_) { setTopicsHistory([]); }
  };

  const loadCalendar = async () => {
    setLoadingCal(true);
    try {
      const r = await api.get(`/marketing/posts/calendar?year=${calMonth.year}&month=${calMonth.month}`);
      setCalPosts(r.data.posts || []);
    } catch (_) { setCalPosts([]); }
    setLoadingCal(false);
  };

  const loadInsights = async () => {
    setLoadingInsights(true);
    try {
      const r = await api.get('/marketing/insights/summary');
      setInsightsSummary(r.data);
    } catch (_) { setInsightsSummary(null); }
    setLoadingInsights(false);
  };

  const loadLinkedinStatus = async () => {
    try {
      const r = await api.get('/linkedin/status');
      setLinkedinStatus(r.data);
      if (r.data.connected) {
        try {
          const orgs = await api.get('/linkedin/organizations');
          setLinkedinOrgs(orgs.data.organizations || []);
          // Bloc 2.5 — n'auto-sélectionne PAS la 1re org. On lit la préférence
          // persistée côté backend. Si vide → aucune sélection (l'utilisateur
          // DOIT choisir explicitement une Page).
          const preferred = r.data.preferred_org_urn;
          if (preferred && (orgs.data.organizations || []).some(o => o.urn === preferred)) {
            setSelectedOrgUrn(preferred);
          } else {
            setSelectedOrgUrn('');   // pas d'auto-sélection sur linkedinOrgs[0]
          }
        } catch (_) {}
      }
    } catch (_) { setLinkedinStatus(null); }
  };

  /** Persistance backend de la Page LinkedIn cible.
   *  Ré-lu par les workers de publication programmée. */
  const savePreferredOrg = async (urn) => {
    if (!urn) return;
    try {
      const r = await api.post('/linkedin/preferred-org', { org_urn: urn });
      setSelectedOrgUrn(r.data.preferred_org_urn);
      // Rafraîchit le status pour que la carte LinkedIn affiche la Page persistée
      loadLinkedinStatus();
      toast.success(`Page LinkedIn : ${r.data.preferred_org_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de persister la Page LinkedIn');
    }
  };

  const connectLinkedin = async () => {
    try {
      const r = await api.get('/linkedin/login');
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'LinkedIn non configuré côté serveur');
    }
  };

  const disconnectLinkedin = async () => {
    if (!window.confirm('Déconnecter LinkedIn ?')) return;
    try {
      await api.post('/linkedin/disconnect');
      setLinkedinStatus(null); setLinkedinOrgs([]); setSelectedOrgUrn('');
      toast.success('LinkedIn déconnecté');
    } catch (_) { toast.error('Échec déconnexion'); }
  };

  const loadMetaStatus = async () => {
    try {
      const r = await api.get('/meta/status');
      setMetaStatus(r.data);
    } catch (_) { setMetaStatus(null); }
  };

  const connectMeta = async () => {
    try {
      const r = await api.get('/meta/login');
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Meta non configuré côté serveur');
    }
  };

  const disconnectMeta = async () => {
    if (!window.confirm('Déconnecter Facebook & Instagram ?')) return;
    try {
      await api.post('/meta/disconnect');
      setMetaStatus(null);
      toast.success('Meta déconnecté');
    } catch (_) { toast.error('Échec déconnexion Meta'); }
  };

  const selectMetaPage = async (pageId) => {
    try {
      await api.post('/meta/select-page', { page_id: pageId });
      toast.success('Page Meta sélectionnée');
      loadMetaStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec sélection page');
    }
  };

  const loadCarouselTemplates = async () => {
    try {
      const r = await api.get('/marketing/carousel-templates');
      setCarouselTemplates(r.data.templates || []);
    } catch (_) { setCarouselTemplates([]); }
  };

  const loadPendingCount = async () => {
    try {
      const r = await api.get('/marketing/agent/pending-count');
      setPendingCount(r.data.count || 0);
      // Charge aussi la liste si count > 0
      if (r.data.count > 0) {
        const l = await api.get('/marketing/agent/pending');
        setPendingPosts(l.data.posts || []);
      } else {
        setPendingPosts([]);
      }
    } catch (_) { setPendingCount(0); setPendingPosts([]); }
  };

  const loadAgentPrefs = async () => {
    try {
      const r = await api.get('/marketing/agent/preferences');
      setAgentPrefs({
        focus_topics: r.data?.focus_topics || [],
        avoid_topics: r.data?.avoid_topics || [],
        tone_hint: r.data?.tone_hint || '',
        visual_variety: r.data?.visual_variety !== false,
      });
    } catch (_) { /* keep defaults */ }
  };

  const saveAgentPrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/marketing/agent/preferences', {
        focus_topics: agentPrefs.focus_topics,
        avoid_topics: agentPrefs.avoid_topics,
        tone_hint: agentPrefs.tone_hint || null,
        visual_variety: !!agentPrefs.visual_variety,
      });
      toast.success('Préférences enregistrées ✓ — elles seront utilisées au prochain lancement.');
      setPrefsOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec enregistrement préférences');
    }
    setSavingPrefs(false);
  };

  // Social Agent V2 — fondation (aucun impact sur V1)
  const loadV2Status = async () => {
    setV2Loading(true);
    try {
      const r = await api.get('/social-agent/status');
      setV2Status(r.data);
      if (r.data?.brand?.brand_id) loadV2Intelligence(r.data.brand.brand_id);
    } catch (_) { setV2Status(null); }
    setV2Loading(false);
  };

  const bootstrapV2Brand = async () => {
    setV2Bootstrapping(true);
    try {
      const r = await api.post('/social-agent/brands/bootstrap-reno-resolve');
      if (r.data?.created) toast.success('Brand Profile Reno Resolve créé ✓');
      else toast.info('Brand Profile Reno Resolve déjà présent (idempotent)');
      await loadV2Status();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du bootstrap');
    }
    setV2Bootstrapping(false);
  };

  const loadV2Intelligence = async (brandId) => {
    if (!brandId) return;
    try {
      const [ri, rs] = await Promise.all([
        api.get(`/social-agent/brands/${brandId}/intelligence`),
        api.get(`/social-agent/brands/${brandId}/sources`),
      ]);
      setV2Intelligence(ri.data?.intelligence || null);
      setV2Sources(rs.data?.sources || []);
    } catch (_) { /* silent */ }
    loadV2Strategy(brandId);
    loadV2Plan(brandId);
    loadV2Inbox(brandId);
  };

  const analyzeV2Brand = async () => {
    const brandId = v2Status?.brand?.brand_id;
    if (!brandId) return;
    if (!window.confirm(
      "L'analyse Brand Brain fera 1 (voire 2) appel(s) LLM. Le cache est activé — un ré-appel identique ne recharge pas. Continuer ?",
    )) return;
    setV2Analyzing(true);
    try {
      const r = await api.post(`/social-agent/brands/${brandId}/analyze`, {
        use_website: !!v2Status?.brand?.website,
        refresh: false,
      });
      if (r.data?.cache_hit) {
        toast.info(`Analyse déjà en cache — 0 appel LLM (confiance ${Math.round((r.data.confidence || 0) * 100)}%)`);
      } else {
        toast.success(`Analyse terminée — ${r.data.llm_calls} appel(s) LLM (confiance ${Math.round((r.data.confidence || 0) * 100)}%)`);
      }
      await Promise.all([loadV2Status(), loadV2Intelligence(brandId)]);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Analyse échouée');
    }
    setV2Analyzing(false);
  };

  const loadV2Strategy = async (brandId) => {
    if (!brandId) return;
    try {
      const r = await api.get(`/social-agent/brands/${brandId}/strategy`);
      setV2Strategy(r.data?.strategy || null);
    } catch (_) { /* silent */ }
  };

  const generateV2Strategy = async () => {
    const brandId = v2Status?.brand?.brand_id;
    if (!brandId) return;
    if (!v2Goals.length) { toast.error('Ajoute au moins un objectif'); return; }
    if (!window.confirm(
      `Générer la stratégie Social Media ? 1 appel LLM (cache activé). Objectifs: ${v2Goals.map(g => g.name).join(', ')}`,
    )) return;
    setV2StratLoading(true);
    try {
      const r = await api.post(`/social-agent/brands/${brandId}/strategy/generate`, {
        goals: v2Goals, refresh: false,
      });
      const s = r.data?.strategy;
      if (r.data?.cache_hit) toast.info(`Stratégie déjà en cache (v${s?.version})`);
      else toast.success(`Stratégie v${s?.version} générée — ${r.data.llm_calls} appel(s) LLM`);
      setV2Strategy(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Génération stratégie échouée');
    }
    setV2StratLoading(false);
  };

  const addGoal = () => {
    const n = (newGoalName || '').trim();
    if (!n) return;
    setV2Goals(g => [...g, { id: `g${Date.now()}`, name: n, priority: g.length + 1 }]);
    setNewGoalName('');
  };
  const removeGoal = (id) => setV2Goals(g => g.filter(x => x.id !== id));

  // ------------------------------------------------------------------
  // V2.3 — Planner
  // ------------------------------------------------------------------
  const loadV2Plan = async (brandId) => {
    if (!brandId) return;
    try {
      const r = await api.get(`/social-agent/brands/${brandId}/planner/current`);
      const plan = r.data?.plan || null;
      setV2Plan(plan);
      // V2.6 — charge les drafts existants pour chaque concept si le plan est approved
      if (plan?.status === 'approved') {
        (plan.concepts || []).forEach(c => loadV2Drafts(c.concept_id));
      }
    } catch (_) { /* silent */ }
  };

  const generateV2Plan = async ({ refresh = false, forceRegenerate = false } = {}) => {
    const brandId = v2Status?.brand?.brand_id;
    if (!brandId) { toast.error('Aucune marque configurée'); return; }
    if (!v2Strategy) { toast.error('Génère d\'abord la stratégie (V2.2)'); return; }
    const bypass = !!(refresh || forceRegenerate);
    const label = forceRegenerate
      ? 'Régénérer la semaine ? Le cache sera IGNORÉ (force_regenerate=true) et de nouveaux angles seront proposés. MAX 1 appel LLM.'
      : `Préparer le planning hebdomadaire ? MAX 1 appel LLM (cache activé). Aucun texte final ni image ne sera généré.`;
    if (!window.confirm(label)) return;
    setV2PlanLoading(true);
    try {
      const r = await api.post(`/social-agent/brands/${brandId}/planner/generate`, {
        refresh: bypass,
      });
      const p = r.data?.plan;
      if (r.data?.cache_hit) toast.info(`Planning en cache (v${p?.version}) — 0 appel LLM`);
      else toast.success(`Planning v${p?.version} ${forceRegenerate ? 'régénéré' : 'généré'} — ${r.data.llm_calls} appel(s) LLM · ${p?.concepts?.length || 0} concept(s)`);
      setV2Plan(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Génération du planning échouée');
    }
    setV2PlanLoading(false);
  };

  const approveV2Plan = async () => {
    if (!v2Plan?.plan_id) return;
    const brandId = v2Status?.brand?.brand_id;
    if (!window.confirm(
      `Valider ce planning ? Aucune génération de contenu ne sera déclenchée. Seuls les concepts sont approuvés.`,
    )) return;
    setV2PlanApproving(true);
    try {
      const r = await api.post(`/social-agent/brands/${brandId}/planner/${v2Plan.plan_id}/approve`, {});
      setV2Plan(r.data?.plan || v2Plan);
      toast.success('Planning approuvé — aucune génération de contenu déclenchée.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approbation échouée');
    }
    setV2PlanApproving(false);
  };

  // ------------------------------------------------------------------
  // V2.4 — Content Inbox
  // ------------------------------------------------------------------
  const loadV2Inbox = async (brandId) => {
    if (!brandId) return;
    setV2InboxLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/social-agent/brands/${brandId}/content-inbox`),
        api.get(`/social-agent/brands/${brandId}/content-inbox/candidates`),
      ]);
      setV2Inbox(r1.data?.items || []);
      setV2InboxCandidates(r2.data?.candidates || []);
    } catch (_) { /* silent */ }
    setV2InboxLoading(false);
  };

  const uploadV2Content = async () => {
    const brandId = v2Status?.brand?.brand_id;
    if (!brandId) { toast.error('Aucune marque'); return; }
    if (!v2InboxFiles.length && !v2InboxText.trim()) {
      toast.error('Ajoute au moins un fichier ou un texte');
      return;
    }
    setV2InboxUploading(true);
    try {
      const fd = new FormData();
      v2InboxFiles.forEach(f => fd.append('files', f));
      fd.append('raw_text', v2InboxText || '');
      fd.append('item_type', v2InboxFiles.length ? 'media' : 'note');
      const r = await api.post(`/social-agent/brands/${brandId}/content-inbox`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Ajouté à l'inbox — ${r.data?.item?.media?.length || 0} média(s). Analyse manuelle disponible.`);
      setV2InboxFiles([]); setV2InboxText('');
      loadV2Inbox(brandId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload échoué');
    }
    setV2InboxUploading(false);
  };

  const addV2Activity = async () => {
    const brandId = v2Status?.brand?.brand_id;
    if (!brandId) { toast.error('Aucune marque'); return; }
    if (!v2ActivityText.trim()) { toast.error('Décris ce qu\'il s\'est passé'); return; }
    try {
      await api.post(`/social-agent/brands/${brandId}/activities`, {
        raw_text: v2ActivityText,
        source_date: v2ActivityDate || null,
        item_type: 'company_activity',
      });
      toast.success('Activité ajoutée.');
      setV2ActivityText(''); setV2ActivityDate('');
      loadV2Inbox(brandId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ajout échoué');
    }
  };

  const processV2Item = async (itemId) => {
    const brandId = v2Status?.brand?.brand_id;
    if (!window.confirm('Analyser cette matière ? MAX 1 appel LLM (cache activé). Aucun texte final ni image générés.')) return;
    setV2InboxProcessingId(itemId);
    try {
      const r = await api.post(`/social-agent/content-inbox/${itemId}/process`);
      const it = r.data?.item;
      toast.success(it?.cache_hit
        ? `Analyse depuis cache — 0 appel LLM (status: ${it.status})`
        : `Analysé — ${r.data?.llm_calls} appel(s) LLM (status: ${it?.status})`);
      loadV2Inbox(brandId);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Analyse échouée');
    }
    setV2InboxProcessingId(null);
  };

  const archiveV2Item = async (itemId) => {
    const brandId = v2Status?.brand?.brand_id;
    if (!window.confirm('Archiver cet item ?')) return;
    try {
      await api.post(`/social-agent/content-inbox/${itemId}/archive`);
      toast.success('Archivé');
      loadV2Inbox(brandId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archivage échoué');
    }
  };

  // V2.5 — Analyse Vision (manuelle, batchée, 1 appel LLM max)
  const analyzeV2Media = async (itemId, forceReanalyze = false) => {
    const brandId = v2Status?.brand?.brand_id;
    const label = forceReanalyze
      ? 'Ré-analyser les médias (force = bypass cache) ? MAX 1 appel Vision (2 max si JSON invalide).'
      : 'Analyser les médias ? MAX 1 appel Vision (cache actif). Aucune caption ni image générée.';
    if (!window.confirm(label)) return;
    setV2VisionAnalyzingId(itemId);
    try {
      const r = await api.post(
        `/social-agent/content-inbox/${itemId}/analyze-media`,
        null,
        { params: { force: forceReanalyze } },
      );
      const va = r.data?.vision_analysis;
      const st = va?.status || 'not_analyzed';
      const review = va?.human_media_review_required;
      const msg = r.data?.cache_hit
        ? `Analyse depuis cache — 0 appel Vision (statut: ${st})`
        : `Analysé — ${r.data?.llm_calls || 0} appel(s) Vision (statut: ${st}${review ? ' · revue humaine requise' : ''})`;
      toast.success(msg);
      loadV2Inbox(brandId);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Analyse Vision échouée');
    }
    setV2VisionAnalyzingId(null);
  };

  // V2.6 — Copywriter helpers
  const loadV2Drafts = async (conceptId) => {
    try {
      const r = await api.get(`/social-agent/concepts/${conceptId}/drafts`);
      const drafts = r.data?.drafts || [];
      setV2DraftsByConcept(prev => ({ ...prev, [conceptId]: drafts }));
      // V2.7 — précharge les reviews des drafts existants
      drafts.forEach(d => {
        if (['pending_review', 'draft'].includes(d.status)) {
          loadV2Reviews(d.draft_id);
        }
      });
      return drafts;
    } catch (e) {
      return [];
    }
  };

  const generateV2Draft = async (brandId, conceptId, refresh = false) => {
    if (v2Plan?.status !== 'approved') {
      toast.error('Le plan doit être approuvé avant de créer le contenu');
      return;
    }
    setV2DraftGenerating(conceptId);
    try {
      const r = await api.post(
        `/social-agent/brands/${brandId}/concepts/${conceptId}/generate-content`,
        { refresh },
      );
      const d = r.data?.draft;
      if (d) {
        setV2DraftsByConcept(prev => ({
          ...prev,
          [conceptId]: [d, ...(prev[conceptId] || []).filter(x => x.draft_id !== d.draft_id)],
        }));
        setV2DraftExpanded(d.draft_id);
        const msg = r.data?.cache_hit
          ? `Draft chargé depuis cache (0 LLM)`
          : `Draft généré · ${r.data?.llm_calls || 0} appel(s) LLM · ${d.platform_variants?.length || 0} variantes`;
        toast.success(msg);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Génération échouée');
    }
    setV2DraftGenerating(null);
  };

  const approveV2Draft = async (draftId, conceptId) => {
    setV2DraftActioning(draftId);
    try {
      const r = await api.post(`/social-agent/drafts/${draftId}/approve`, {});
      toast.success('Draft approuvé');
      setV2DraftsByConcept(prev => ({
        ...prev,
        [conceptId]: (prev[conceptId] || []).map(d => d.draft_id === draftId ? r.data.draft : d),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approbation refusée');
    }
    setV2DraftActioning(null);
  };

  const rejectV2Draft = async (draftId, conceptId) => {
    const reason = window.prompt('Raison du rejet ?', '');
    if (reason === null) return;
    setV2DraftActioning(draftId);
    try {
      const r = await api.post(`/social-agent/drafts/${draftId}/reject`, { reason });
      toast.success('Draft rejeté');
      setV2DraftsByConcept(prev => ({
        ...prev,
        [conceptId]: (prev[conceptId] || []).map(d => d.draft_id === draftId ? r.data.draft : d),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rejet échoué');
    }
    setV2DraftActioning(null);
  };

  const regenerateV2Draft = async (draftId, conceptId) => {
    if (!window.confirm('Régénérer le contenu ? Un nouvel appel LLM sera effectué.')) return;
    setV2DraftActioning(draftId);
    try {
      const r = await api.post(`/social-agent/drafts/${draftId}/regenerate`);
      const d = r.data?.draft;
      if (d) {
        setV2DraftsByConcept(prev => ({
          ...prev,
          [conceptId]: [d, ...(prev[conceptId] || []).filter(x => x.draft_id !== d.draft_id)],
        }));
        setV2DraftExpanded(d.draft_id);
        toast.success(`Régénéré · ${r.data?.llm_calls || 0} appel(s) LLM`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Régénération échouée');
    }
    setV2DraftActioning(null);
  };

  // ------------------------------------------------------------------
  // V3.1 — Sélection plateformes (persistée backend) et preview (locale)
  // ------------------------------------------------------------------
  /** Retourne la liste plateformes cochées pour un draft (source canonique
   *  = backend, fallback = toutes les variantes existantes si non défini). */
  const getSelectedPlatforms = (draft) => {
    if (!draft) return [];
    if (selectedPlatformsByDraft[draft.draft_id] !== undefined) {
      return selectedPlatformsByDraft[draft.draft_id];
    }
    if (Array.isArray(draft.selected_platforms) && draft.selected_platforms.length) {
      return draft.selected_platforms;
    }
    return (draft.platform_variants || []).map(v => v.platform);
  };

  /** Bascule d'une plateforme dans la sélection puis persistance backend. */
  const toggleDraftPlatform = async (draft, conceptId, platform) => {
    if (!draft) return;
    const available = (draft.platform_variants || []).map(v => v.platform);
    if (!available.includes(platform)) {
      toast.error(`Aucune variante '${platform}' disponible pour ce draft`);
      return;
    }
    const current = getSelectedPlatforms(draft);
    const has = current.includes(platform);
    const next = has ? current.filter(p => p !== platform) : [...current, platform];
    if (next.length === 0) {
      toast.error('Au moins une plateforme doit rester cochée pour programmer/publier');
      return;
    }
    setSelectedPlatformsByDraft(prev => ({ ...prev, [draft.draft_id]: next }));
    setSavingSelectedByDraft(prev => ({ ...prev, [draft.draft_id]: true }));
    try {
      const r = await api.patch(`/social-agent/drafts/${draft.draft_id}/selected-platforms`, {
        selected_platforms: next,
      });
      const d = r.data?.draft;
      if (d) {
        setV2DraftsByConcept(prev => ({
          ...prev,
          [conceptId]: (prev[conceptId] || []).map(x => x.draft_id === d.draft_id ? d : x),
        }));
      }
    } catch (e) {
      setSelectedPlatformsByDraft(prev => ({ ...prev, [draft.draft_id]: current }));
      toast.error(e?.response?.data?.detail || 'Sauvegarde de la sélection échouée');
    }
    setSavingSelectedByDraft(prev => ({ ...prev, [draft.draft_id]: false }));
  };

  /** Preview visible (n'affecte JAMAIS selectedPlatforms). */
  const setPreviewPlatform = (draftId, platform) => {
    setPreviewPlatformByDraft(prev => ({ ...prev, [draftId]: platform }));
  };
  const getPreviewPlatform = (draft) => {
    if (!draft) return null;
    const stored = previewPlatformByDraft[draft.draft_id];
    const variants = (draft.platform_variants || []).map(v => v.platform);
    if (stored && variants.includes(stored)) return stored;
    return variants[0] || null;
  };



  // ============================================================
  // V2.7 — Editorial Reviewer
  // ============================================================
  const loadV2Reviews = async (draftId) => {
    try {
      const r = await api.get(`/social-agent/drafts/${draftId}/reviews`);
      setV2ReviewsByDraft(prev => ({ ...prev, [draftId]: r.data?.reviews || [] }));
      return r.data?.reviews || [];
    } catch (e) {
      return [];
    }
  };

  const runV2Review = async (draftId, refresh = false) => {
    setV2ReviewLoading(draftId);
    try {
      const r = await api.post(`/social-agent/drafts/${draftId}/review`, { refresh });
      const rv = r.data?.review;
      if (rv) {
        setV2ReviewsByDraft(prev => ({
          ...prev,
          [draftId]: [rv, ...(prev[draftId] || []).filter(x => x.review_id !== rv.review_id)],
        }));
        const msg = r.data?.cache_hit
          ? `Review chargée depuis cache (0 LLM)`
          : `Review générée · ${r.data?.llm_calls || 0} appel(s) LLM · ${(rv.blocking_findings || []).length} blocking · ${(rv.warnings || []).length} warning`;
        toast.success(msg);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review échouée');
    }
    setV2ReviewLoading(null);
  };

  const applyV2Revision = async (reviewId, draftId, conceptId) => {
    if (!window.confirm("Appliquer la révision proposée ? Une nouvelle version du draft sera créée.")) return;
    setV2ReviewActioning(reviewId);
    try {
      const r = await api.post(`/social-agent/reviews/${reviewId}/apply`, {});
      const rv = r.data?.review;
      const d = r.data?.draft;
      if (rv && d) {
        setV2ReviewsByDraft(prev => ({
          ...prev,
          [draftId]: [rv, ...(prev[draftId] || []).filter(x => x.review_id !== rv.review_id)],
        }));
        setV2DraftsByConcept(prev => ({
          ...prev,
          [conceptId]: [d, ...(prev[conceptId] || []).filter(x => x.draft_id !== d.draft_id)],
        }));
        toast.success(`Révision appliquée · draft v${d.version} · ${(r.data?.applied_fields || []).length} champ(s) modifié(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply échoué");
    }
    setV2ReviewActioning(null);
  };

  const applyV2Finding = async (reviewId, findingId, draftId, conceptId) => {
    setV2ReviewActioning(`${reviewId}::${findingId}`);
    try {
      const r = await api.post(`/social-agent/reviews/${reviewId}/findings/${findingId}/apply`, {});
      const rv = r.data?.review;
      const d = r.data?.draft;
      if (rv && d) {
        setV2ReviewsByDraft(prev => ({
          ...prev,
          [draftId]: [rv, ...(prev[draftId] || []).filter(x => x.review_id !== rv.review_id)],
        }));
        setV2DraftsByConcept(prev => ({
          ...prev,
          [conceptId]: [d, ...(prev[conceptId] || []).filter(x => x.draft_id !== d.draft_id)],
        }));
        toast.success(`Correction appliquée · draft v${d.version}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correction échouée");
    }
    setV2ReviewActioning(null);
  };

  const overrideV2Finding = async (reviewId, findingId, draftId) => {
    const reason = window.prompt("Raison de l'override ? (obligatoire)");
    if (!reason) return;
    setV2ReviewActioning(`${reviewId}::${findingId}`);
    try {
      const r = await api.post(`/social-agent/reviews/${reviewId}/findings/${findingId}/override`, { reason });
      const rv = r.data?.review;
      if (rv) {
        setV2ReviewsByDraft(prev => ({
          ...prev,
          [draftId]: [rv, ...(prev[draftId] || []).filter(x => x.review_id !== rv.review_id)],
        }));
        toast.success("Finding ignoré (audit conservé)");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Override refusé");
    }
    setV2ReviewActioning(null);
  };

  const addTopic = (kind, value) => {
    const v = (value || '').trim();
    if (!v) return;
    setAgentPrefs(prev => {
      const key = kind === 'focus' ? 'focus_topics' : 'avoid_topics';
      if ((prev[key] || []).includes(v)) return prev;
      return { ...prev, [key]: [...(prev[key] || []), v] };
    });
    if (kind === 'focus') setFocusInput(''); else setAvoidInput('');
  };

  const removeTopic = (kind, value) => {
    setAgentPrefs(prev => {
      const key = kind === 'focus' ? 'focus_topics' : 'avoid_topics';
      return { ...prev, [key]: (prev[key] || []).filter(t => t !== value) };
    });
  };

  const validateOne = async (postId) => {
    try {
      await api.post(`/marketing/agent/validate/${postId}`);
      toast.success('Post programmé ✓');
      loadPendingCount();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec validation');
    }
  };

  const rejectOne = async (postId, theme) => {
    if (!window.confirm(`Rejeter et supprimer le post "${theme}" ?`)) return;
    try {
      await api.delete(`/marketing/posts/${postId}`);
      toast.success('Post rejeté');
      loadPendingCount();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec suppression');
    }
  };

  const runAgentNow = async () => {
    if (agentRunning) return;
    if (!window.confirm('Lancer l\'agent Marketing autonome maintenant ? Il va générer 5 posts (textes + visuels) pour la semaine prochaine. Compte 3-5 minutes.')) return;
    setAgentRunning(true);
    try {
      await api.post('/marketing/agent/run-now');
      toast.success('Agent lancé. Reviens dans 3-5 min pour valider les 5 posts.');
      // Poll pending count every 20s pendant 5 min
      let ticks = 0;
      const timer = setInterval(async () => {
        ticks++;
        await loadPendingCount();
        if (ticks >= 15) clearInterval(timer);
      }, 20000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec lancement agent');
    }
    setAgentRunning(false);
  };

  const validateWeek = async () => {
    if (!window.confirm(`Valider et programmer les ${pendingCount} posts générés par l'agent ?`)) return;
    setValidating(true);
    try {
      const r = await api.post('/marketing/agent/validate-week', {});
      toast.success(`${r.data.validated} post(s) programmé(s)`);
      loadPendingCount();
      if (view === 'library') loadPosts();
      if (view === 'calendar') loadCalendar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec validation');
    }
    setValidating(false);
  };

  const rebrandPending = async () => {
    if (!window.confirm(`Ré-appliquer la signature Reno Resolve (logo + barre or) sur les ${pendingCount} posts en attente ?`)) return;
    const toastId = toast.loading(`Re-branding de ${pendingCount} visuel(s)…`);
    try {
      const r = await api.post('/marketing/agent/rebrand-pending');
      toast.dismiss(toastId);
      const { rebranded = 0, skipped = 0, errors = [] } = r.data || {};
      if (rebranded > 0) toast.success(`${rebranded} visuel(s) re-brandé(s) ✓${skipped ? ` (${skipped} déjà brandés)` : ''}`);
      else if (skipped > 0) toast.info(`Tous les visuels étaient déjà brandés (${skipped})`);
      else toast.warning('Aucun visuel re-brandé');
      if (errors.length) console.warn('[rebrand] errors:', errors);
      loadPendingCount();
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(e?.response?.data?.detail || 'Échec re-branding');
    }
  };

  const runInsightsAnalyze = async () => {
    setAnalyzingInsights(true);
    try {
      const r = await api.post('/marketing/insights/analyze');
      setInsightsAnalysis(r.data);
      if (r.data.posts_analyzed === 0) {
        toast.info(r.data.verdict);
      } else {
        toast.success('Analyse IA générée');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec analyse IA');
    }
    setAnalyzingInsights(false);
  };

  const [planningWeek, setPlanningWeek] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  const submitPlanWeek = async (customSlots, startDate) => {
    setPlanningWeek(true);
    try {
      const payload = {};
      if (customSlots) payload.slots = customSlots;
      if (startDate) payload.start_date = startDate;
      const r = await api.post('/marketing/plan-week', payload);
      toast.success(`${r.data.planned} posts planifiés pour la semaine du ${new Date(r.data.week_start).toLocaleDateString('fr-FR')}`);
      const d = new Date(r.data.week_start);
      setCalMonth({ year: d.getFullYear(), month: d.getMonth() + 1 });
      setShowPlanModal(false);
      setView('calendar');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec planification');
    }
    setPlanningWeek(false);
  };

  const openPost = async (postOrId) => {
    // Accepte un id ou un objet post
    const id = typeof postOrId === 'string' ? postOrId : postOrId.post_id;
    try {
      const r = await api.get(`/marketing/posts/${id}`);
      setPreviewPost(r.data);
    } catch (e) {
      toast.error('Impossible de charger le post');
    }
  };

  const savePerformance = async (postId, platform, values) => {
    try {
      await api.patch(`/marketing/posts/${postId}/performance`, { platform, ...values });
      toast.success(`Stats ${platform} enregistrées`);
      // Recharge le post et l'insights
      openPost(postId);
      if (view === 'insights') loadInsights();
    } catch (e) {
      toast.error('Échec enregistrement stats');
    }
  };

  const scanNews = async () => {
    setLoadingTopics(true);
    try {
      const r = await api.get('/marketing/news/scan');
      setScanSummary(r.data);
      toast.success(`${r.data.total} article(s) analysé(s) depuis ${r.data.categories.filter(c => c.articles.length).length} sources`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec scan actualités');
    }
    setLoadingTopics(false);
  };

  const suggestTopics = async (force = false) => {
    setLoadingTopics(true);
    setSuggestedTopics([]);
    try {
      const r = await api.post('/marketing/topics/suggest', { count: 3, force_refresh: force });
      setSuggestedTopics(r.data.topics || []);
      toast.success(`${r.data.topics?.length || 0} sujet(s) proposé(s) parmi ${r.data.scanned_articles} articles`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec suggestion de sujets');
    }
    setLoadingTopics(false);
  };

  const applyTopicToComposer = async (topic, autoGenerate = true) => {
    const newBrief = {
      ...brief,
      theme: topic.angle,
      context: [
        topic.content_hook,
        topic.suggested_context,
      ].filter(Boolean).join('\n\n'),
      call_to_action: topic.suggested_cta || brief.call_to_action,
      source_name: topic.source?.name || '',
      source_url: topic.source?.url || '',
      needs_verification: !!topic.needs_verification,
      content_type: topic.content_type || null,
      objective: topic.objective || null,
    };
    setBrief(newBrief);
    setActiveTopicId(topic.topic_id);
    api.post(`/marketing/topics/${topic.topic_id}/mark-used`).catch(() => {});
    setView('compose');
    toast.success('Sujet appliqué — génération automatique en cours…');
    if (autoGenerate) {
      await generateText(newBrief, true);
    }
  };

  const togglePlatform = (id) => {
    setBrief(prev => ({
      ...prev,
      platforms: prev.platforms.includes(id) ? prev.platforms.filter(p => p !== id) : [...prev.platforms, id],
    }));
  };

  const [themeError, setThemeError] = useState(false);

  const generateText = async (customBrief = null, autoImage = false) => {
    // Garde-fou : si appelé depuis un onClick, le 1er arg est un SyntheticEvent, pas un brief
    if (customBrief && typeof customBrief === 'object' && (customBrief.nativeEvent || customBrief._reactName)) {
      customBrief = null;
    }
    const useBrief = customBrief || brief;
    console.log('[Composer] generateText clicked', {
      theme: useBrief.theme, platforms: useBrief.platforms, tone: useBrief.tone,
    });
    if (!useBrief.theme || !useBrief.theme.trim()) {
      setThemeError(true);
      toast.error('Précise un thème pour le post (champ obligatoire)');
      // Focus le champ + scroll pour l'utilisateur mobile
      try {
        const el = document.querySelector('[data-testid="mkt-theme"]');
        if (el) { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      } catch (_) {}
      return null;
    }
    setThemeError(false);
    if (!useBrief.platforms || useBrief.platforms.length === 0) {
      toast.error('Sélectionne au moins une plateforme (LinkedIn, Facebook ou Instagram)');
      return null;
    }
    setGeneratingText(true);
    try {
      const r = await api.post('/marketing/generate', { ...useBrief, include_image_prompt: true });
      const v = r.data.variants || {};
      const ip = r.data.image_prompt || '';
      setVariants(v);
      if (ip) setImagePrompt(ip);
      // Carrousel : stocke les slides
      if (r.data.carousel && Array.isArray(r.data.slides)) {
        setCarouselSlides(r.data.slides);
        setCarouselTemplateLabel(r.data.template_label || '');
        toast.success(`Carrousel de ${r.data.slides.length} slides généré ✓`);
      } else {
        setCarouselSlides([]);
        setCarouselTemplateLabel('');
        toast.success(`${Object.keys(v).length} variante(s) générée(s)${ip ? ' + prompt image' : ''}`);
      }
      if (autoImage && ip) {
        // Enchaîne la génération d'image (peut prendre 60s)
        triggerImage(ip);
      }
      return { variants: v, image_prompt: ip, slides: r.data.slides };
    } catch (e) {
      console.error('[Composer] generateText error', e);
      toast.error(e?.response?.data?.detail || e?.message || 'Échec génération texte');
      return null;
    } finally {
      setGeneratingText(false);
    }
  };

  const triggerImage = async (promptOverride = null) => {
    const prompt = (promptOverride || imagePrompt || brief.theme).trim();
    if (!prompt) { toast.error('Précise un prompt image ou un thème'); return; }
    setGeneratingImage(true);
    try {
      const r = await api.post('/marketing/image', { prompt, aspect_ratio: imageRatio, apply_brand: applyBrand });
      setImageUrl(r.data.image_url);
      toast.success('Image générée via Lovart');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec Lovart');
    }
    setGeneratingImage(false);
  };

  const generateImage = () => triggerImage();

  const copyText = (platform) => {
    navigator.clipboard.writeText(variants[platform] || '');
    toast.success('Texte copié — colle-le dans ton app ' + platform);
  };

  const generateCarouselImages = async () => {
    if (!carouselSlides.length) {
      toast.error('Aucun slide à illustrer');
      return;
    }
    setGeneratingCarouselImages(true);
    const toastId = toast.loading(
      `Génération des ${carouselSlides.length} visuels Lovart (charte Reno Resolve)… ~${carouselSlides.length * 20}s`
    );
    try {
      const r = await api.post('/marketing/carousel/generate-images', {
        slides: carouselSlides.map(s => ({ image_prompt: s.image_prompt, title: s.title })),
        aspect_ratio: carouselImageRatio,
        apply_brand: applyBrand,
      });
      const images = r.data.images || [];
      // Merge dans carouselSlides
      const updated = carouselSlides.map((s, i) => {
        const img = images[i] || {};
        return { ...s, image_url: img.image_url || null, image_error: img.error || null };
      });
      setCarouselSlides(updated);
      toast.dismiss(toastId);
      const ok = r.data.success_count || 0;
      if (ok === carouselSlides.length) {
        toast.success(`${ok}/${carouselSlides.length} visuels générés ✓`);
      } else if (ok > 0) {
        toast.warning(`${ok}/${carouselSlides.length} visuels OK — relance pour compléter les autres`);
      } else {
        toast.error('Aucun visuel généré — Lovart en timeout ? Réessaie dans 1 min.');
      }
    } catch (e) {
      toast.dismiss(toastId);
      console.error('[Carousel images]', e);
      toast.error(e?.response?.data?.detail || 'Échec génération visuels carrousel');
    } finally {
      setGeneratingCarouselImages(false);
    }
  };

  const savePost = async () => {
    if (!Object.keys(variants).length) { toast.error('Génère au moins un texte avant d\'enregistrer'); return; }
    setSaving(true);
    try {
      await api.post('/marketing/posts', {
        theme: brief.theme,
        variants,
        image_url: imageUrl || null,
        image_prompt: imagePrompt || null,
        status: scheduledAt ? 'scheduled' : 'ready',
        scheduled_at: scheduledAt || null,
        notes: brief.context,
        content_type: brief.content_type || null,
        objective: brief.objective || null,
        source_name: brief.source_name || null,
        source_url: brief.source_url || null,
        carousel: carouselSlides.length > 0,
        slides: carouselSlides.length > 0 ? carouselSlides : null,
      });
      toast.success(scheduledAt ? 'Post programmé' : 'Post enregistré dans la bibliothèque');
      setVariants({}); setCarouselSlides([]); setCarouselTemplateLabel(''); setImageUrl(''); setImagePrompt(''); setScheduledAt('');
      setBrief({ ...brief, theme: '', context: '', call_to_action: '',
                 source_name: '', source_url: '', needs_verification: false });
    } catch (e) { toast.error('Échec sauvegarde'); }
    setSaving(false);
  };

  const deletePost = async (post_id) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    try { await api.delete(`/marketing/posts/${post_id}`); loadPosts(); toast.success('Supprimé'); }
    catch (_) { toast.error('Échec'); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="marketing-studio">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-blue-700" /> Marketing Studio
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Agent autonome connecté aux flux BTP (Batiactu, Le Moniteur, FFB, CAPEB, ADEME, OPPBTP,
            Ministère écologie, Construction21, Cerema) — propose 3 sujets/jour et génère
            posts LinkedIn / Facebook / Instagram + visuels Lovart charte Reno Resolve.
          </p>
        </div>
        <div className="flex gap-2 bg-slate-100 p-1 rounded-lg">
          {[
            { id: 'agent',    label: 'Agent IA' },
            { id: 'compose',  label: 'Composer' },
            { id: 'calendar', label: 'Calendrier' },
            { id: 'library',  label: 'Bibliothèque' },
            { id: 'insights', label: 'Insights' },
          ].map(t => (
            <button key={t.id} onClick={() => setView(t.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${view === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`mkt-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'agent' && (
        <div className="space-y-6" data-testid="mkt-agent-view">
          {/* Bandeau de commande */}
          <div className="bg-gradient-to-br from-[#1C49A6] to-[#0a4bab] rounded-2xl p-6 text-white shadow-lg">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-start gap-3 max-w-2xl">
                <div className="bg-white/15 backdrop-blur rounded-xl p-3">
                  <Bot className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    Agent éditorial autonome
                    <span className="bg-amber-400 text-slate-900 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">GPT-4o</span>
                  </h2>
                  <p className="text-sm text-blue-100 mt-1 leading-relaxed">
                    Chaque appel, l'agent scanne les flux BTP hiérarchisés, filtre selon la charte
                    éditoriale Reno Resolve, écarte les sujets déjà traités (30 jours) et propose
                    3 angles prêts à devenir des posts — ou <strong>planifie toute la semaine</strong> en un clic.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={scanNews} disabled={loadingTopics}
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="mkt-agent-scan">
                  {loadingTopics ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Radar className="w-4 h-4 mr-2" />}
                  Scanner l'actu
                </Button>
                <Button onClick={() => suggestTopics(false)} disabled={loadingTopics}
                  className="bg-amber-400 text-slate-900 hover:bg-amber-300 font-semibold"
                  data-testid="mkt-agent-suggest">
                  {loadingTopics ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Proposer 3 sujets
                </Button>
                <Button onClick={() => setShowPlanModal(true)} disabled={planningWeek || loadingTopics}
                  className="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold"
                  data-testid="mkt-agent-plan-week">
                  {planningWeek ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-2" />}
                  Planifier ma semaine
                </Button>
              </div>
            </div>
          </div>

          {/* Préférences Agent — sujets prioritaires, à éviter, variété visuelle */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="mkt-agent-prefs-card">
            <button
              type="button"
              onClick={() => setPrefsOpen(o => !o)}
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition rounded-2xl"
              data-testid="mkt-agent-prefs-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-amber-700" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                    Préférences éditoriales de l'agent
                    {(agentPrefs.focus_topics.length > 0 || agentPrefs.avoid_topics.length > 0 || agentPrefs.tone_hint) && (
                      <span className="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-semibold">
                        actives
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    Sujets à privilégier, à éviter, ton spécifique et rotation des styles visuels
                  </div>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${prefsOpen ? 'rotate-180' : ''}`} />
            </button>

            {prefsOpen && (
              <div className="border-t border-slate-100 p-5 space-y-4">
                {/* Sujets prioritaires */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">
                    ✅ Sujets à privilégier
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                    {agentPrefs.focus_topics.length === 0 && (
                      <span className="text-xs text-slate-400 italic">Aucun — l'agent décide selon l'actu BTP</span>
                    )}
                    {agentPrefs.focus_topics.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-2 py-0.5 rounded-full"
                            data-testid={`mkt-focus-chip-${t}`}>
                        {t}
                        <button onClick={() => removeTopic('focus', t)} className="hover:text-red-600" data-testid={`mkt-focus-remove-${t}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={focusInput}
                      onChange={e => setFocusInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTopic('focus', focusInput); } }}
                      placeholder="ex : ITE, MaPrimeRénov', Fonds Vert, Écoles..."
                      className="flex-1 border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                      data-testid="mkt-focus-input"
                    />
                    <Button size="sm" variant="outline" onClick={() => addTopic('focus', focusInput)}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid="mkt-focus-add">
                      + Ajouter
                    </Button>
                  </div>
                </div>

                {/* Sujets à éviter */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">
                    🚫 Sujets à éviter
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                    {agentPrefs.avoid_topics.length === 0 && (
                      <span className="text-xs text-slate-400 italic">Aucun</span>
                    )}
                    {agentPrefs.avoid_topics.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-800 text-xs px-2 py-0.5 rounded-full"
                            data-testid={`mkt-avoid-chip-${t}`}>
                        {t}
                        <button onClick={() => removeTopic('avoid', t)} className="hover:text-red-600" data-testid={`mkt-avoid-remove-${t}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={avoidInput}
                      onChange={e => setAvoidInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTopic('avoid', avoidInput); } }}
                      placeholder="ex : accidents chantier, polémiques..."
                      className="flex-1 border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-red-200 focus:border-red-400"
                      data-testid="mkt-avoid-input"
                    />
                    <Button size="sm" variant="outline" onClick={() => addTopic('avoid', avoidInput)}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            data-testid="mkt-avoid-add">
                      + Ajouter
                    </Button>
                  </div>
                </div>

                {/* Ton */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">
                    🎙️ Consigne de ton (optionnel)
                  </label>
                  <input
                    type="text"
                    value={agentPrefs.tone_hint || ''}
                    onChange={e => setAgentPrefs(p => ({ ...p, tone_hint: e.target.value }))}
                    placeholder="ex : plus institutionnel, adressé aux élus / plus terrain, orienté résultats chiffrés"
                    className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                    data-testid="mkt-tone-hint-input"
                  />
                </div>

                {/* Variété visuelle */}
                <label className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100/60 transition"
                       data-testid="mkt-visual-variety-toggle">
                  <input
                    type="checkbox"
                    checked={!!agentPrefs.visual_variety}
                    onChange={e => setAgentPrefs(p => ({ ...p, visual_variety: e.target.checked }))}
                    className="w-4 h-4 mt-0.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900">Varier les styles visuels</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      Rotation automatique sur les 5 posts hebdo : <strong>photo éditoriale</strong> · <strong>infographie chiffrée</strong> · <strong>carte typographique</strong> · <strong>schéma technique</strong> · <strong>gros plan matière</strong>.
                      Désactive pour ne garder que des photos.
                    </div>
                  </div>
                </label>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button variant="outline" onClick={() => setPrefsOpen(false)}
                          data-testid="mkt-prefs-cancel">
                    Annuler
                  </Button>
                  <Button onClick={saveAgentPrefs} disabled={savingPrefs}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="mkt-prefs-save">
                    {savingPrefs ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                    Enregistrer
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Agent Social Media V2 — Fondation (V2.0)                          */}
          {/* La V1 continue de fonctionner. Cette section est en LECTURE.      */}
          {/* ---------------------------------------------------------------- */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-slate-100 rounded-2xl border border-slate-700 shadow-md overflow-hidden"
               data-testid="mkt-social-agent-v2-card">
            <div className="p-5 flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-300 text-[10px] font-bold tracking-wider">
                    V2 · FONDATION
                  </span>
                  <span className="text-xs text-slate-400">
                    {v2Status?.agent_version ? `agent v${v2Status.agent_version}` : '—'}
                  </span>
                </div>
                <h3 className="text-base font-bold">Agent Social Media V2</h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Nouvelle architecture (Director + capacités + mémoire + décisions) qui cohabite avec l'agent V1 actuel. Aucune génération n'est encore effectuée par la V2.
                </p>
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  onClick={loadV2Status}
                  disabled={v2Loading}
                  variant="outline"
                  size="sm"
                  className="bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700"
                  data-testid="mkt-v2-refresh"
                >
                  {v2Loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                  Voir l'état de l'agent
                </Button>
                {v2Status && v2Status.state === 'no_brand' && (
                  <Button
                    onClick={bootstrapV2Brand}
                    disabled={v2Bootstrapping}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                    data-testid="mkt-v2-bootstrap"
                  >
                    {v2Bootstrapping ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    Initialiser Reno Resolve
                  </Button>
                )}
              </div>
            </div>

            {v2Status && (
              <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                {/* Brand */}
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Marque</div>
                  <div className="font-semibold text-slate-100">
                    {v2Status.brand?.name || <span className="text-amber-300">Aucune</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {v2Status.brand?.positioning || '—'}
                  </div>
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {(v2Status.brand?.visual_identity?.primary_colors || []).map(c => (
                      <span key={c} className="inline-flex items-center gap-1 text-[10px] bg-slate-700/60 border border-slate-600 rounded-full px-2 py-0.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                        {c}
                      </span>
                    ))}
                    {(v2Status.brand?.visual_identity?.accent_colors || []).map(c => (
                      <span key={c} className="inline-flex items-center gap-1 text-[10px] bg-slate-700/60 border border-slate-600 rounded-full px-2 py-0.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: c }} />
                        {c}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Capabilities */}
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 md:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
                    Capacités enregistrées ({v2Status.capabilities?.length || 0})
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(v2Status.capabilities || []).map(c => (
                      <div key={c.name} className="flex items-center gap-2" data-testid={`mkt-v2-cap-${c.name}`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.status === 'ready' ? 'bg-emerald-400' : c.status === 'placeholder' ? 'bg-amber-400' : 'bg-slate-500'}`} />
                        <span className="font-medium capitalize">{c.name.replace('_', ' ')}</span>
                        <span className="text-[10px] text-slate-500 ml-auto">{c.status}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Meta rows */}
                <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
                  <div className="bg-slate-800/40 rounded-md p-2 border border-slate-700">
                    <div className="text-slate-400 uppercase text-[9px] tracking-wider">État</div>
                    <div className={`font-semibold ${v2Status.state === 'ready' ? 'text-emerald-300' : 'text-amber-300'}`}>
                      {v2Status.state === 'ready' ? 'Prêt' : 'Marque à initialiser'}
                    </div>
                  </div>
                  <div className="bg-slate-800/40 rounded-md p-2 border border-slate-700">
                    <div className="text-slate-400 uppercase text-[9px] tracking-wider">Mémoires</div>
                    <div className="font-semibold text-slate-100">{v2Status.memory_count}</div>
                  </div>
                  <div className="bg-slate-800/40 rounded-md p-2 border border-slate-700">
                    <div className="text-slate-400 uppercase text-[9px] tracking-wider">Dernier run</div>
                    <div className="font-semibold text-slate-100">
                      {v2Status.last_run ? v2Status.last_run.status : '—'}
                    </div>
                  </div>
                  <div className="bg-slate-800/40 rounded-md p-2 border border-slate-700">
                    <div className="text-slate-400 uppercase text-[9px] tracking-wider">Plateformes</div>
                    <div className="font-semibold text-slate-100">
                      {['linkedin', 'facebook', 'instagram'].filter(p => v2Status.platforms_detected?.[p]).length} / 3
                    </div>
                  </div>
                </div>

                {/* Intelligence de marque (V2.1) */}
                {v2Status.brand && (
                  <div className="md:col-span-3 border-t border-slate-700 pt-4 mt-1"
                       data-testid="mkt-brand-brain-section">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Intelligence de marque · V2.1</div>
                        <div className="font-bold text-slate-100 text-sm">Brand Brain</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${v2Intelligence ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>
                          {v2Intelligence ? 'Analysée' : 'Non analysée'}
                        </span>
                        {v2Intelligence && (
                          <span className="text-[10px] text-slate-400">
                            Confiance : {Math.round((v2Intelligence.confidence || 0) * 100)}%
                          </span>
                        )}
                        <Button
                          onClick={analyzeV2Brand}
                          disabled={v2Analyzing}
                          size="sm"
                          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-xs"
                          data-testid="mkt-brand-brain-analyze"
                        >
                          {v2Analyzing
                            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analyse…</>
                            : (v2Intelligence
                                ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Actualiser</>
                                : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Analyser ma marque</>)}
                        </Button>
                      </div>
                    </div>

                    {/* Résumé Intelligence */}
                    {v2Intelligence && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Positionnement</div>
                          <div className="text-slate-100">{v2Intelligence.market_position || v2Intelligence.brand_summary || '—'}</div>
                        </div>
                        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Audiences</div>
                          <div className="flex flex-wrap gap-1">
                            {(v2Intelligence.audiences || []).slice(0, 4).map(a => (
                              <span key={a.name} className="bg-slate-700/60 border border-slate-600 rounded-full px-2 py-0.5">
                                {a.name}
                              </span>
                            ))}
                            {!v2Intelligence.audiences?.length && <span className="text-slate-500">—</span>}
                          </div>
                        </div>
                        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Ton</div>
                          <div className="text-slate-100">{v2Intelligence.communication?.tone || '—'}</div>
                        </div>
                        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Piliers éditoriaux</div>
                          <div className="flex flex-wrap gap-1">
                            {(v2Intelligence.editorial_identity?.pillars || []).slice(0, 6).map(p => (
                              <span key={p} className="bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-full px-2 py-0.5">
                                {p}
                              </span>
                            ))}
                            {!v2Intelligence.editorial_identity?.pillars?.length && <span className="text-slate-500">—</span>}
                          </div>
                        </div>
                        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Style visuel</div>
                          <div className="text-slate-100">{v2Intelligence.visual_identity?.style_summary || '—'}</div>
                        </div>
                        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Opportunités Social Media</div>
                          <div className="text-slate-200 space-y-0.5">
                            {(v2Intelligence.social_media_opportunities || []).slice(0, 3).map((o, i) => (
                              <div key={i} className="flex items-start gap-1"><span className="text-amber-300">•</span><span>{o}</span></div>
                            ))}
                            {!v2Intelligence.social_media_opportunities?.length && <span className="text-slate-500">—</span>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sources */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap text-[10px]">
                      <span className="text-slate-400 uppercase tracking-wider">Sources :</span>
                      {v2Sources.length === 0 && <span className="text-slate-500">—</span>}
                      {['manual', 'existing_profile', 'user_preferences', 'website'].map(t => {
                        const n = v2Sources.filter(s => s.source_type === t).length;
                        if (n === 0) return null;
                        return (
                          <span key={t} className="bg-slate-700/70 border border-slate-600 rounded-full px-2 py-0.5 text-slate-200">
                            {t === 'manual' && 'Saisie manuelle'}
                            {t === 'existing_profile' && 'Profil'}
                            {t === 'user_preferences' && 'Préférences'}
                            {t === 'website' && `Site web (${n})`}
                          </span>
                        );
                      })}
                    </div>

                    {/* ---------- V2.2 Stratégie Social Media ---------- */}
                    <div className="mt-5 border-t border-slate-700 pt-4"
                         data-testid="mkt-strategy-section">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400">Stratégie Social Media · V2.2</div>
                          <div className="font-bold text-slate-100 text-sm">Strategist</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${v2Strategy ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>
                            {v2Strategy ? `Active · v${v2Strategy.version}` : 'Non configurée'}
                          </span>
                          <Button onClick={generateV2Strategy} disabled={v2StratLoading}
                            size="sm"
                            className="bg-blue-500 hover:bg-blue-400 text-slate-900 font-semibold text-xs"
                            data-testid="mkt-strategy-generate">
                            {v2StratLoading
                              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Génération…</>
                              : (v2Strategy
                                  ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Actualiser</>
                                  : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Générer ma stratégie</>)}
                          </Button>
                        </div>
                      </div>

                      {/* Goals editor */}
                      <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 mb-3">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1.5">Objectifs (priorité par ordre)</div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {v2Goals.length === 0 && <span className="text-xs text-slate-500 italic">Aucun objectif — ajoute-en un</span>}
                          {v2Goals.map((g, i) => (
                            <span key={g.id} className="inline-flex items-center gap-1 bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs px-2 py-0.5 rounded-full"
                                  data-testid={`mkt-goal-${g.id}`}>
                              <span className="font-bold text-[10px]">#{i + 1}</span>
                              {g.name}
                              <button onClick={() => removeGoal(g.id)} className="hover:text-red-300">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input type="text" value={newGoalName}
                                 onChange={e => setNewGoalName(e.target.value)}
                                 onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGoal(); } }}
                                 placeholder="ex : Développer marchés collectivités"
                                 className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-slate-100"
                                 data-testid="mkt-goal-input" />
                          <Button size="sm" variant="outline" onClick={addGoal}
                                  className="text-xs bg-slate-800 border-slate-600 text-slate-100"
                                  data-testid="mkt-goal-add">+ Ajouter</Button>
                        </div>
                      </div>

                      {/* Strategy summary */}
                      {v2Strategy && (
                        <div className="space-y-3">
                          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-[11px]">
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Résumé stratégique</div>
                            <div className="text-slate-100">{v2Strategy.strategic_summary || '—'}</div>
                          </div>

                          {/* Platforms */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                            {Object.entries(v2Strategy.platform_strategies || {}).map(([name, p]) => (
                              <div key={name} className={`rounded-lg p-3 border ${p.enabled ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-800/20 border-slate-700 opacity-50'}`}
                                   data-testid={`mkt-strategy-platform-${name}`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="capitalize font-bold text-slate-100">{name}</span>
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">{p.weekly_target || 0}/sem</span>
                                </div>
                                <div className="text-slate-300 mb-1">{p.role || '—'}</div>
                                <div className="text-[10px] text-slate-400">Audiences : {(p.primary_audiences || []).slice(0, 2).join(', ') || '—'}</div>
                                <div className="text-[10px] text-slate-400">Formats : {(p.preferred_formats || []).slice(0, 3).join(', ') || '—'}</div>
                              </div>
                            ))}
                          </div>

                          {/* Pillars */}
                          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-2">Piliers éditoriaux ({(v2Strategy.content_pillars || []).length})</div>
                            <div className="space-y-1.5">
                              {(v2Strategy.content_pillars || []).map(p => (
                                <div key={p.id} className="flex items-center gap-2 text-[11px]">
                                  <span className="w-14 text-right font-bold text-amber-300">{Math.round(p.weight)}%</span>
                                  <div className="flex-1 bg-slate-900 rounded-sm h-4 overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300"
                                         style={{ width: `${Math.min(100, p.weight)}%` }} />
                                  </div>
                                  <span className="w-40 text-slate-200 truncate">{p.name}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Content mix */}
                          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-2">Mix contenu</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[10px]">
                              {Object.entries(v2Strategy.content_mix || {}).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between bg-slate-900/60 rounded px-2 py-1">
                                  <span className="capitalize text-slate-300">{k.replace('_', ' ')}</span>
                                  <span className="font-bold text-slate-100">{Math.round(v)}%</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Commercial policy */}
                          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-[11px]">
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1">Politique commerciale</div>
                            <div className="flex items-center gap-3 flex-wrap text-slate-200">
                              <span>Max commercial : <strong className="text-amber-300">{Math.round(v2Strategy.commercial_policy?.max_commercial_percentage || 0)}%</strong></span>
                              <span>Soft sell : <strong>{v2Strategy.commercial_policy?.soft_sell_preferred ? 'Oui' : 'Non'}</strong></span>
                              {(v2Strategy.commercial_policy?.cta_rules || []).length > 0 && (
                                <span className="text-slate-400">CTA : {v2Strategy.commercial_policy.cta_rules.slice(0, 2).join(' · ')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ---------- V2.4 Matière terrain ---------- */}
                    {v2Strategy && (
                      <div className="mt-5 border-t border-slate-700 pt-4"
                           data-testid="mkt-inbox-section">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400">Matière terrain · V2.4</div>
                            <div className="font-bold text-slate-100 text-sm">Content Inbox & Company Activity</div>
                            <div className="text-[10px] text-slate-500">Alimente le cerveau du Planner. Aucun texte final ni image générés.</div>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                            <span>{v2Inbox.length} item(s)</span>
                            <span className="text-emerald-300">{v2InboxCandidates.length} prêt(s) pour le planner</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          {/* Ajouter média + texte */}
                          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1.5">Ajouter du contenu (photos / vidéos / documents)</div>
                            <label htmlFor="v2-inbox-files"
                              className="block border-2 border-dashed border-slate-600 rounded-lg p-3 text-center cursor-pointer hover:border-amber-500/60 transition-colors text-[11px] text-slate-300"
                              data-testid="mkt-inbox-dropzone">
                              <UploadCloud className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                              Déposez vos photos, vidéos ou documents
                              <input id="v2-inbox-files" type="file" multiple hidden
                                accept="image/*,video/*,application/pdf"
                                onChange={e => setV2InboxFiles(Array.from(e.target.files || []))}
                                data-testid="mkt-inbox-file-input" />
                              {v2InboxFiles.length > 0 && (
                                <div className="mt-1 text-amber-300">{v2InboxFiles.length} fichier(s) sélectionné(s)</div>
                              )}
                            </label>
                            <textarea rows={2}
                              className="mt-2 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-[11px] text-slate-200 focus:outline-none focus:border-amber-500/60"
                              placeholder="Décris rapidement ce qu'il se passe (chantier, lieu, type de travaux…)"
                              value={v2InboxText}
                              onChange={e => setV2InboxText(e.target.value)}
                              data-testid="mkt-inbox-text" />
                            <div className="flex justify-end mt-2">
                              <Button onClick={uploadV2Content} disabled={v2InboxUploading}
                                size="sm" className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold text-xs"
                                data-testid="mkt-inbox-upload-btn">
                                {v2InboxUploading
                                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Envoi…</>
                                  : <><Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter à l'Inbox</>}
                              </Button>
                            </div>
                            <div className="text-[9px] text-slate-500 mt-1">Pas d'analyse LLM automatique. Cliquez "Analyser" ensuite. Vision (V2.5) déclenchable manuellement par item.</div>
                          </div>

                          {/* Ajouter une activité sans média */}
                          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-1.5">Activité rapide (sans média)</div>
                            <textarea rows={3}
                              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-[11px] text-slate-200 focus:outline-none focus:border-amber-500/60"
                              placeholder="Qu'est-ce qui s'est passé ? Ex : RDV avec les services techniques d'une commune..."
                              value={v2ActivityText}
                              onChange={e => setV2ActivityText(e.target.value)}
                              data-testid="mkt-activity-text" />
                            <div className="flex items-center gap-2 mt-2">
                              <input type="date"
                                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-200"
                                value={v2ActivityDate}
                                onChange={e => setV2ActivityDate(e.target.value)}
                                data-testid="mkt-activity-date" />
                              <div className="flex-1" />
                              <Button onClick={addV2Activity} size="sm"
                                className="bg-slate-100 hover:bg-white text-slate-900 font-semibold text-xs"
                                data-testid="mkt-activity-add-btn">
                                <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter l'activité
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Inbox list */}
                        {v2Inbox.length === 0 ? (
                          <div className="text-[11px] text-slate-500 italic bg-slate-800/30 border border-slate-700 rounded-lg p-3">
                            Aucune matière ajoutée. Le Planner remonte alors des <em>opportunity_gaps</em> pour real_media / company_activity / content_inbox.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
                                data-testid="mkt-inbox-list">
                            {v2Inbox.map((it, idx) => {
                              const st = it.status;
                              const badge = st === 'ready' ? 'bg-emerald-500/20 text-emerald-200'
                                : st === 'needs_information' ? 'bg-amber-500/20 text-amber-200'
                                : st === 'processed' ? 'bg-blue-500/20 text-blue-200'
                                : st === 'archived' ? 'bg-slate-700 text-slate-300'
                                : 'bg-slate-700 text-slate-200';
                              const usable = ['ready', 'processed'].includes(st);
                              const hasMedia = (it.media || []).some(m => m.type === 'image');
                              const va = it.vision_analysis;
                              const vst = va?.status || 'not_analyzed';
                              const vBadge = vst === 'analyzed' ? 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40'
                                : vst === 'needs_review' ? 'bg-orange-500/20 text-orange-200 border-orange-500/40'
                                : vst === 'failed' ? 'bg-red-500/20 text-red-200 border-red-500/40'
                                : vst === 'pending' ? 'bg-slate-700 text-slate-300 border-slate-600'
                                : 'bg-slate-800 text-slate-400 border-slate-700';
                              const vLabel = vst === 'analyzed' ? 'Vision · OK'
                                : vst === 'needs_review' ? 'Vision · À vérifier'
                                : vst === 'failed' ? 'Vision · Échec'
                                : vst === 'pending' ? 'Vision · En cours'
                                : 'Vision · Non analysé';
                              return (
                                <div key={it.item_id}
                                     className="bg-slate-800/60 border border-slate-700 rounded-lg p-2.5 text-[11px]"
                                     data-testid={`mkt-inbox-card-${idx}`}>
                                  <div className="flex items-start gap-2 mb-1.5">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-slate-100 font-bold truncate">{it.title || it.raw_text?.slice(0, 40) || `Item ${idx + 1}`}</div>
                                      <div className="text-[9px] text-slate-500 mt-0.5">
                                        {it.item_type} · {(it.media || []).length} média · {new Date(it.created_at).toLocaleDateString('fr-FR')}
                                      </div>
                                    </div>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${badge}`}>{st}</span>
                                  </div>
                                  {hasMedia && (
                                    <div className="flex items-center gap-1 mb-1.5">
                                      <span className={`px-1.5 py-0.5 rounded border text-[9px] font-semibold ${vBadge}`}
                                            data-testid={`mkt-inbox-vision-badge-${idx}`}>
                                        {vLabel}
                                      </span>
                                    </div>
                                  )}
                                  {it.raw_text && (
                                    <div className="text-slate-300 text-[10px] mb-1 line-clamp-2">{it.raw_text}</div>
                                  )}
                                  {(it.verified_facts || []).length > 0 && (
                                    <div className="text-[9px] text-emerald-300 mb-0.5">
                                      ✓ {(it.verified_facts || []).slice(0, 2).join(' · ')}
                                    </div>
                                  )}
                                  {(it.unverified_claims || []).length > 0 && (
                                    <div className="text-[9px] text-amber-300 mb-0.5">
                                      ⚠ {(it.unverified_claims || []).slice(0, 2).join(' · ')}
                                    </div>
                                  )}
                                  {(it.missing_information || []).length > 0 && (
                                    <div className="text-[9px] text-slate-400 italic">
                                      ? {(it.missing_information || []).slice(0, 2).join(' · ')}
                                    </div>
                                  )}
                                  {/* V2.5 — Résumé visuel + alertes privacy/qualité */}
                                  {va && (va.aggregated_observations || []).length > 0 && (
                                    <div className="text-[9px] text-cyan-300 mt-1 line-clamp-2"
                                         data-testid={`mkt-inbox-vision-obs-${idx}`}>
                                      👁 {(va.aggregated_observations || []).slice(0, 3).join(' · ')}
                                    </div>
                                  )}
                                  {va?.human_media_review_required && (va.review_reasons || []).length > 0 && (
                                    <div className="text-[9px] text-orange-300 mt-0.5 line-clamp-2"
                                         data-testid={`mkt-inbox-vision-review-${idx}`}>
                                      🔒 Revue humaine requise : {(va.review_reasons || []).slice(0, 2).join(' · ')}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <span className={`text-[9px] ${usable ? 'text-emerald-300' : 'text-slate-500'}`}>
                                      {usable ? 'Utilisable pour le planning' : (st === 'archived' ? 'Archivé' : 'Non analysé')}
                                    </span>
                                    <div className="flex-1" />
                                    {st !== 'archived' && (
                                      <>
                                        <button onClick={() => processV2Item(it.item_id)}
                                          disabled={v2InboxProcessingId === it.item_id}
                                          className="text-[10px] px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
                                          data-testid={`mkt-inbox-analyze-${idx}`}>
                                          {v2InboxProcessingId === it.item_id ? '…' : (it.cache_hit || it.last_processed_at ? 'Ré-analyser texte' : 'Analyser texte')}
                                        </button>
                                        {hasMedia && (
                                          <button onClick={() => analyzeV2Media(it.item_id, vst === 'analyzed' || vst === 'needs_review')}
                                            disabled={v2VisionAnalyzingId === it.item_id}
                                            className="text-[10px] px-2 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-50"
                                            data-testid={`mkt-inbox-analyze-media-${idx}`}>
                                            {v2VisionAnalyzingId === it.item_id
                                              ? '…'
                                              : (vst === 'not_analyzed' ? 'Analyser médias' : 'Ré-analyser médias')}
                                          </button>
                                        )}
                                        <button onClick={() => archiveV2Item(it.item_id)}
                                          className="text-[10px] px-2 py-0.5 rounded border border-slate-600 text-slate-400 hover:text-slate-200"
                                          data-testid={`mkt-inbox-archive-${idx}`}>
                                          Archiver
                                        </button>
                                      </>
                                    )}
                                  </div>
                                  {it.times_used > 0 && (
                                    <div className="text-[9px] text-slate-500 mt-1">Utilisé {it.times_used} fois par le planner</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ---------- V2.3 Planning IA ---------- */}
                    {v2Strategy && (
                      <div className="mt-5 border-t border-slate-700 pt-4"
                           data-testid="mkt-planner-section">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400">Planning IA · V2.3</div>
                            <div className="font-bold text-slate-100 text-sm">Intelligent Planner</div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              v2Plan?.status === 'approved' ? 'bg-emerald-500/25 text-emerald-200'
                              : v2Plan?.status === 'pending_approval' ? 'bg-amber-500/20 text-amber-200'
                              : v2Plan?.status === 'failed' ? 'bg-red-500/20 text-red-300'
                              : v2Plan ? 'bg-slate-700 text-slate-200'
                              : 'bg-slate-700 text-slate-400'
                            }`} data-testid="mkt-planner-status-badge">
                              {v2Plan?.status === 'approved' ? `Approuvé · v${v2Plan.version}`
                                : v2Plan?.status === 'pending_approval' ? `À valider · v${v2Plan.version}`
                                : v2Plan?.status === 'failed' ? 'Échec' 
                                : v2Plan ? `Brouillon · v${v2Plan.version}`
                                : 'Aucun planning'}
                            </span>
                            <Button onClick={() => generateV2Plan({ refresh: false, forceRegenerate: false })} disabled={v2PlanLoading}
                              size="sm"
                              className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold text-xs"
                              data-testid="mkt-planner-generate">
                              {v2PlanLoading
                                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Préparation…</>
                                : (v2Plan
                                    ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Actualiser</>
                                    : <><CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Préparer ma semaine</>)}
                            </Button>
                            {v2Plan && (
                              <Button
                                onClick={() => generateV2Plan({ forceRegenerate: true })}
                                disabled={v2PlanLoading}
                                size="sm"
                                variant="outline"
                                className="border-orange-500/40 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20 text-xs"
                                data-testid="mkt-planner-force-regenerate"
                                title="Ignore le cache et propose de nouveaux angles éditoriaux"
                              >
                                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Régénérer la semaine
                              </Button>
                            )}
                            {v2Plan && ['draft', 'pending_approval'].includes(v2Plan?.status) && (
                              <Button onClick={approveV2Plan} disabled={v2PlanApproving}
                                size="sm" variant="outline"
                                className="bg-emerald-500/10 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20 text-xs"
                                data-testid="mkt-planner-approve">
                                {v2PlanApproving
                                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Validation…</>
                                  : <><ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Valider le planning</>}
                              </Button>
                            )}
                          </div>
                        </div>

                        {!v2Plan && (
                          <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-[11px] text-slate-300">
                            Le Planner génère un plan hebdomadaire de <strong>concepts éditoriaux</strong>
                            (pas de textes ni images finaux). Chaque concept peut être décliné sur plusieurs
                            plateformes via des <em>PlatformIntent</em>.
                            <br />
                            <span className="text-slate-400">
                              Cible : {v2Strategy?.posting_strategy?.weekly_target || 5} concept(s) / semaine ·
                              Cache activé · Max 1 appel LLM.
                            </span>
                          </div>
                        )}

                        {v2Plan && (
                          <div className="space-y-3" data-testid="mkt-planner-content">
                            {/* Meta */}
                            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-[11px] flex flex-wrap gap-3">
                              <span><strong className="text-slate-100">Semaine du</strong> {v2Plan.week_start}</span>
                              <span><strong className="text-slate-100">Cible</strong> {v2Plan.weekly_target} concept(s)</span>
                              <span><strong className="text-slate-100">Concepts</strong> {v2Plan.concepts?.length || 0}</span>
                              <span><strong className="text-slate-100">Candidates</strong> {v2Plan.top_candidates?.length || 0}</span>
                              <span><strong className="text-slate-100">LLM</strong> {v2Plan.cache_hit ? 'cache' : `${v2Plan.llm_calls} appel(s)`}</span>
                            </div>

                            {/* Concepts */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {(v2Plan.concepts || []).map((c, idx) => (
                                <div key={c.concept_id}
                                     className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-[11px] hover:border-amber-500/40 transition-colors"
                                     data-testid={`mkt-planner-concept-${idx}`}>
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-[9px] font-bold text-amber-300">#{c.priority}</span>
                                    <span className="text-slate-100 font-bold">{c.title}</span>
                                    {c.verification_required && (
                                      <span className="text-[9px] uppercase bg-red-500/20 text-red-200 px-1.5 py-0.5 rounded">à vérifier</span>
                                    )}
                                  </div>
                                  <div className="text-slate-300 mb-1.5">{c.editorial_angle}</div>
                                  <div className="flex flex-wrap gap-1.5 text-[10px] mb-1.5">
                                    {(() => {
                                      const st = c.source_type;
                                      const label = st === 'real_media' ? 'Terrain'
                                        : st === 'company_activity' ? 'Activité Reno Resolve'
                                        : st === 'content_inbox' ? 'Inbox'
                                        : st === 'regulation' ? 'Réglementation'
                                        : st === 'industry_news' ? 'Actualité'
                                        : st === 'evergreen' ? 'Evergreen'
                                        : st === 'seasonal' ? 'Saisonnier'
                                        : st;
                                      const cls = st === 'real_media' ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
                                        : st === 'company_activity' ? 'bg-blue-500/15 border-blue-500/40 text-blue-200'
                                        : st === 'content_inbox' ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-200'
                                        : st === 'regulation' ? 'bg-red-500/15 border-red-500/40 text-red-200'
                                        : 'bg-slate-900 border-slate-700 text-slate-300';
                                      return (
                                        <span className={`border rounded px-1.5 py-0.5 ${cls}`}
                                              data-testid={`mkt-concept-source-${idx}`}>
                                          {label}
                                        </span>
                                      );
                                    })()}
                                    {c.pillar_name && (
                                      <span className="bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5 text-blue-200">
                                        {c.pillar_name}
                                      </span>
                                    )}
                                    {typeof c.day_of_week === 'number' && (
                                      <span className="bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-slate-300">
                                        {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'][c.day_of_week]}
                                        {c.time_slot_hint ? ` · ${c.time_slot_hint}` : ''}
                                      </span>
                                    )}
                                  </div>

                                  {/* Platforms */}
                                  <div className="space-y-1 mb-1.5">
                                    {(c.platforms || []).map((pi, i) => (
                                      <div key={i} className="flex items-center gap-2 text-[10px] bg-slate-900/70 rounded px-2 py-1">
                                        <span className="capitalize font-bold text-slate-200 w-16">{pi.platform}</span>
                                        <span className="text-[9px] uppercase bg-slate-700 rounded px-1 text-slate-300">{pi.format}</span>
                                        <span className="text-slate-300 flex-1 truncate">{pi.angle}</span>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Media requirements */}
                                  {(c.media_requirements || []).length > 0 && (
                                    <div className="text-[10px] text-slate-400 mb-1">
                                      <strong>Médias attendus :</strong> {c.media_requirements.join(' · ')}
                                    </div>
                                  )}

                                  {/* Source facts */}
                                  {(c.source_facts || []).length > 0 && (
                                    <div className="text-[10px] text-slate-500 italic">
                                      <strong>Sources :</strong> {c.source_facts.slice(0, 2).join(' · ')}
                                    </div>
                                  )}

                                  {/* Opportunity gaps */}
                                  {(c.opportunity_gaps || []).length > 0 && (
                                    <div className="mt-1.5 text-[10px] text-amber-300 flex items-center gap-1">
                                      <ShieldCheck className="w-3 h-3" />
                                      <span>{c.opportunity_gaps.slice(0, 2).join(' · ')}</span>
                                    </div>
                                  )}

                                  {/* V2.6 — Copywriter actions */}
                                  {v2Plan.status === 'approved' && (() => {
                                    const drafts = v2DraftsByConcept[c.concept_id] || [];
                                    const active = drafts.find(d => ['pending_review', 'draft'].includes(d.status)) || drafts[0];
                                    const expanded = active && v2DraftExpanded === active.draft_id;
                                    return (
                                      <div className="mt-2 pt-2 border-t border-slate-700">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                          {!active ? (
                                            <button
                                              onClick={() => generateV2Draft(v2Plan.brand_id, c.concept_id)}
                                              disabled={v2DraftGenerating === c.concept_id}
                                              className="text-[10px] px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
                                              data-testid={`mkt-concept-create-content-${idx}`}
                                            >
                                              {v2DraftGenerating === c.concept_id ? '…' : '✏️ Créer le contenu'}
                                            </button>
                                          ) : (
                                            <>
                                              <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                                active.status === 'approved' ? 'bg-emerald-500/20 text-emerald-200'
                                                : active.status === 'rejected' ? 'bg-red-500/20 text-red-200'
                                                : active.status === 'pending_review' ? 'bg-amber-500/20 text-amber-200'
                                                : 'bg-slate-700 text-slate-300'
                                              }`}
                                              data-testid={`mkt-concept-draft-status-${idx}`}>
                                                {active.status} · v{active.version}
                                              </span>
                                              <button
                                                onClick={() => setV2DraftExpanded(expanded ? null : active.draft_id)}
                                                className="text-[10px] px-2 py-0.5 rounded border border-slate-600 text-slate-300 hover:text-slate-100"
                                                data-testid={`mkt-concept-draft-toggle-${idx}`}
                                              >
                                                {expanded ? 'Replier' : 'Voir'}
                                              </button>
                                            </>
                                          )}
                                        </div>
                                        {expanded && active && (
                                          <div className="bg-slate-950/60 border border-slate-700 rounded p-2 mt-1 space-y-2">
                                            {active.master_angle && (
                                              <div className="text-[10px]">
                                                <strong className="text-amber-300">Angle :</strong> <span className="text-slate-200">{active.master_angle}</span>
                                              </div>
                                            )}
                                            {active.master_message && (
                                              <div className="text-[10px]">
                                                <strong className="text-amber-300">Message :</strong> <span className="text-slate-200">{active.master_message}</span>
                                              </div>
                                            )}
                                            {/* Facts / Observations / Inferences séparés */}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[9px]">
                                              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-1.5">
                                                <div className="uppercase text-emerald-300 font-bold mb-1">Facts vérifiés ({(active.verified_facts_used || []).length})</div>
                                                {(active.verified_facts_used || []).slice(0, 4).map((f, i) => <div key={i} className="text-slate-200">• {f}</div>)}
                                              </div>
                                              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded p-1.5">
                                                <div className="uppercase text-cyan-300 font-bold mb-1">Observations Vision ({(active.visual_observations_used || []).length})</div>
                                                {(active.visual_observations_used || []).slice(0, 4).map((o, i) => <div key={i} className="text-slate-300">• {o}</div>)}
                                              </div>
                                              <div className="bg-slate-800 border border-slate-700 rounded p-1.5">
                                                <div className="uppercase text-slate-400 font-bold mb-1">Inférences ({(active.inferences_used || []).length})</div>
                                                {(active.inferences_used || []).slice(0, 4).map((i, k) => <div key={k} className="text-slate-400 italic">• {i}</div>)}
                                              </div>
                                            </div>
                                            {/* V3.1 — Sélection plateformes (persistée) + Preview séparée */}
                                            <div className="bg-slate-950/40 border border-slate-700 rounded p-2 space-y-2">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">
                                                  Plateformes de publication
                                                </span>
                                                {savingSelectedByDraft[active.draft_id] && (
                                                  <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                                                )}
                                                {(() => {
                                                  const availablePlatforms = (active.platform_variants || []).map(v => v.platform);
                                                  const selected = getSelectedPlatforms(active);
                                                  return ['linkedin', 'facebook', 'instagram'].map(pl => {
                                                    const hasVariant = availablePlatforms.includes(pl);
                                                    const checked = selected.includes(pl);
                                                    return (
                                                      <label key={pl}
                                                        className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border cursor-pointer transition ${
                                                          !hasVariant ? 'opacity-40 cursor-not-allowed border-slate-800 text-slate-500'
                                                          : checked ? 'bg-amber-500/15 border-amber-500/40 text-amber-100'
                                                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-amber-500/30'
                                                        }`}
                                                        data-testid={`mkt-draft-platform-toggle-${pl}-${idx}`}
                                                      >
                                                        <input
                                                          type="checkbox"
                                                          disabled={!hasVariant}
                                                          checked={checked}
                                                          onChange={() => toggleDraftPlatform(active, c.concept_id, pl)}
                                                          className="w-3 h-3 accent-amber-400"
                                                          data-testid={`mkt-draft-platform-checkbox-${pl}-${idx}`}
                                                        />
                                                        <span className="capitalize font-semibold">{pl}</span>
                                                      </label>
                                                    );
                                                  });
                                                })()}
                                              </div>
                                              <div className="text-[9px] text-slate-500 italic">
                                                Au moins 1 plateforme requise. Les variantes non cochées ne seront pas programmées / publiées.
                                              </div>
                                            </div>

                                            {/* V3.1 — Onglets Preview (n'affecte PAS la sélection) */}
                                            {(active.platform_variants || []).length > 0 && (() => {
                                              const previewPl = getPreviewPlatform(active);
                                              const previewVariant = (active.platform_variants || []).find(v => v.platform === previewPl);
                                              return (
                                                <div className="space-y-1.5">
                                                  <div className="flex items-center gap-1 flex-wrap">
                                                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mr-1">
                                                      Aperçu
                                                    </span>
                                                    {(active.platform_variants || []).map((v) => (
                                                      <button
                                                        key={v.platform}
                                                        type="button"
                                                        onClick={() => setPreviewPlatform(active.draft_id, v.platform)}
                                                        className={`text-[10px] px-2 py-0.5 rounded border capitalize font-semibold transition ${
                                                          previewPl === v.platform
                                                            ? 'bg-slate-100 text-slate-900 border-slate-100'
                                                            : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-500'
                                                        }`}
                                                        data-testid={`mkt-draft-preview-tab-${v.platform}-${idx}`}
                                                      >
                                                        {v.platform}
                                                      </button>
                                                    ))}
                                                  </div>
                                                  {previewVariant ? (
                                                    <div
                                                      className="bg-slate-900 border border-slate-700 rounded p-2"
                                                      data-testid={`mkt-draft-preview-content-${previewVariant.platform}-${idx}`}
                                                    >
                                                      <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="text-[10px] font-bold capitalize text-amber-300">{previewVariant.platform}</span>
                                                        <span className="text-[9px] uppercase bg-slate-700 rounded px-1 text-slate-300">{previewVariant.format}</span>
                                                        {previewVariant.tone && <span className="text-[9px] italic text-slate-400">{previewVariant.tone}</span>}
                                                        <span className="text-[9px] text-slate-500 ml-auto">{previewVariant.character_count} car.</span>
                                                      </div>
                                                      {previewVariant.hook && <div className="text-[11px] text-slate-100 font-bold whitespace-pre-wrap">{previewVariant.hook}</div>}
                                                      {previewVariant.body && <div className="text-[11px] text-slate-300 mt-0.5 whitespace-pre-wrap">{previewVariant.body}</div>}
                                                      {previewVariant.cta && <div className="text-[11px] text-amber-300 mt-0.5">→ {previewVariant.cta}</div>}
                                                      {(previewVariant.hashtags || []).length > 0 && (
                                                        <div className="text-[10px] text-cyan-300 mt-0.5">#{previewVariant.hashtags.join(' #')}</div>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="text-[10px] text-slate-500 italic">Aucune variante à prévisualiser.</div>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                            {/* Creative Brief */}
                                            {active.creative_brief && (
                                              <div className="bg-blue-500/5 border border-blue-500/30 rounded p-1.5 text-[10px]">
                                                <div className="uppercase text-blue-300 font-bold mb-0.5">Brief créatif</div>
                                                <div className="text-slate-300"><strong>Type :</strong> {active.creative_brief.recommended_asset_type} · IA nécessaire : {active.creative_brief.ai_generation_needed ? 'oui' : 'non'}</div>
                                                {(active.creative_brief.selected_media_ids || []).length > 0 && (
                                                  <div className="text-slate-300"><strong>Médias sélectionnés :</strong> {active.creative_brief.selected_media_ids.join(', ')}</div>
                                                )}
                                                {active.creative_brief.desired_visual_story && (
                                                  <div className="text-slate-400 italic">"{active.creative_brief.desired_visual_story}"</div>
                                                )}
                                                {(active.creative_brief.privacy_requirements || []).length > 0 && (
                                                  <div className="text-orange-300 mt-0.5">🔒 {active.creative_brief.privacy_requirements.slice(0, 2).join(' · ')}</div>
                                                )}
                                                {(active.creative_brief.crop_recommendations || []).length > 0 && (
                                                  <div className="text-slate-400 mt-0.5">📐 {active.creative_brief.crop_recommendations.slice(0, 2).join(' · ')}</div>
                                                )}
                                              </div>
                                            )}
                                            {/* Verification items */}
                                            {(active.verification_items || []).length > 0 && (
                                              <div className="text-[10px]">
                                                <div className="uppercase text-red-300 font-bold mb-0.5">À vérifier ({(active.verification_items || []).length})</div>
                                                {(active.verification_items || []).slice(0, 5).map((vi, i) => (
                                                  <div key={i} className={`text-[9px] ${vi.severity === 'blocking' ? 'text-red-300' : 'text-amber-300'}`}>
                                                    [{vi.severity}] {vi.topic}: {vi.claim}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                            {/* Warnings */}
                                            {(active.warnings || []).length > 0 && (
                                              <div className="text-[9px] text-slate-500 italic">
                                                {active.warnings.slice(0, 3).map((w, i) => <div key={i}>⚠ {w}</div>)}
                                              </div>
                                            )}
                                            {/* ============================================
                                                V2.7 — Editorial Reviewer Panel
                                            ============================================ */}
                                            {(() => {
                                              const reviews = v2ReviewsByDraft[active.draft_id] || [];
                                              const openReview = reviews.find(r => r.status === 'open') || reviews[0];
                                              const isLoading = v2ReviewLoading === active.draft_id;
                                              return (
                                                <div className="mt-2 pt-2 border-t border-slate-700 space-y-1.5">
                                                  <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-[9px] uppercase tracking-wider text-blue-300 font-bold">
                                                      Editorial Review (V2.7)
                                                    </span>
                                                    {openReview && (
                                                      <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                                        openReview.recommendation_status === 'blocked' ? 'bg-red-500/20 text-red-200'
                                                        : openReview.recommendation_status === 'needs_edits' ? 'bg-amber-500/20 text-amber-200'
                                                        : 'bg-emerald-500/20 text-emerald-200'
                                                      }`} data-testid={`mkt-review-status-${idx}`}>
                                                        {openReview.recommendation_status.replace(/_/g, ' ')}
                                                      </span>
                                                    )}
                                                    <button
                                                      onClick={() => runV2Review(active.draft_id, !!openReview)}
                                                      disabled={isLoading}
                                                      className="text-[10px] px-2 py-0.5 rounded border border-blue-600/40 bg-blue-600/10 text-blue-200 hover:bg-blue-600/20 disabled:opacity-50 ml-auto"
                                                      data-testid={`mkt-review-run-${idx}`}
                                                    >
                                                      {isLoading ? '…' : openReview ? '↻ Ré-analyser' : '🧐 Lancer review'}
                                                    </button>
                                                  </div>
                                                  {openReview && (
                                                    <>
                                                      <div className="text-[9px] text-slate-500">
                                                        {(openReview.blocking_findings || []).length} blocking ·
                                                        {' '}{(openReview.warnings || []).length} warning ·
                                                        {' '}{(openReview.suggestions || []).length} suggestion
                                                        {openReview.llm_calls > 0 && ` · ${openReview.llm_calls} LLM`}
                                                        {openReview.cache_hit && ' · cache'}
                                                      </div>

                                                      {/* À corriger — blocking */}
                                                      {(openReview.blocking_findings || []).length > 0 && (
                                                        <div className="bg-red-500/10 border border-red-500/40 rounded p-1.5"
                                                             data-testid={`mkt-review-blocking-${idx}`}>
                                                          <div className="uppercase text-red-300 font-bold text-[9px] mb-1">
                                                            À corriger ({openReview.blocking_findings.length})
                                                          </div>
                                                          {openReview.blocking_findings.slice(0, 8).map((f, i) => (
                                                            <div key={i} className="text-[10px] mb-1 last:mb-0">
                                                              <div className="text-red-200">
                                                                <strong>[{f.category.replace(/_/g, ' ')}]</strong>{' '}
                                                                <span className="text-slate-300">« {f.exact_excerpt} »</span>
                                                              </div>
                                                              <div className="text-[9px] text-slate-400">{f.explanation}</div>
                                                              {f.suggested_action && (
                                                                <div className="text-[9px] text-slate-400 italic">→ {f.suggested_action}</div>
                                                              )}
                                                              <div className="flex gap-1 mt-0.5 flex-wrap">
                                                                {f.suggested_replacement && (
                                                                  <button
                                                                    onClick={() => applyV2Finding(openReview.review_id, f.finding_id, active.draft_id, c.concept_id)}
                                                                    disabled={v2ReviewActioning === `${openReview.review_id}::${f.finding_id}`}
                                                                    className="text-[9px] px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                                                                  >Appliquer correction</button>
                                                                )}
                                                                {f.overridable ? (
                                                                  <button
                                                                    onClick={() => overrideV2Finding(openReview.review_id, f.finding_id, active.draft_id)}
                                                                    className="text-[9px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-300 hover:text-slate-100"
                                                                  >Ignorer</button>
                                                                ) : (
                                                                  <span className="text-[9px] text-red-400 italic">non-overridable</span>
                                                                )}
                                                              </div>
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}

                                                      {/* À vérifier — warnings */}
                                                      {(openReview.warnings || []).length > 0 && (
                                                        <div className="bg-amber-500/10 border border-amber-500/40 rounded p-1.5"
                                                             data-testid={`mkt-review-warnings-${idx}`}>
                                                          <div className="uppercase text-amber-300 font-bold text-[9px] mb-1">
                                                            À vérifier ({openReview.warnings.length})
                                                          </div>
                                                          {openReview.warnings.slice(0, 8).map((f, i) => (
                                                            <div key={i} className="text-[10px] mb-1 last:mb-0">
                                                              <div className="text-amber-200">
                                                                <strong>[{f.category.replace(/_/g, ' ')}]</strong>{' '}
                                                                <span className="text-slate-300">« {f.exact_excerpt} »</span>
                                                                {f.override_action === 'ignored' && (
                                                                  <span className="text-[9px] text-slate-500 italic ml-1">(ignoré)</span>
                                                                )}
                                                              </div>
                                                              <div className="text-[9px] text-slate-400">{f.explanation}</div>
                                                              {f.suggested_action && (
                                                                <div className="text-[9px] text-slate-400 italic">→ {f.suggested_action}</div>
                                                              )}
                                                              {!f.override_action && (
                                                                <div className="flex gap-1 mt-0.5 flex-wrap">
                                                                  {f.suggested_replacement && (
                                                                    <button
                                                                      onClick={() => applyV2Finding(openReview.review_id, f.finding_id, active.draft_id, c.concept_id)}
                                                                      className="text-[9px] px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                                                                    >Appliquer</button>
                                                                  )}
                                                                  <button
                                                                    onClick={() => overrideV2Finding(openReview.review_id, f.finding_id, active.draft_id)}
                                                                    className="text-[9px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-300 hover:text-slate-100"
                                                                  >Ignorer</button>
                                                                </div>
                                                              )}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}

                                                      {/* Suggestions */}
                                                      {(openReview.suggestions || []).length > 0 && (
                                                        <div className="bg-slate-800/60 border border-slate-700 rounded p-1.5">
                                                          <div className="uppercase text-slate-400 font-bold text-[9px] mb-1">
                                                            Suggestions ({openReview.suggestions.length})
                                                          </div>
                                                          {openReview.suggestions.slice(0, 5).map((f, i) => (
                                                            <div key={i} className="text-[10px] mb-1 last:mb-0">
                                                              <div className="text-slate-300">
                                                                <strong>[{f.category.replace(/_/g, ' ')}]</strong>{' '}
                                                                « {f.exact_excerpt} »
                                                              </div>
                                                              <div className="text-[9px] text-slate-500 italic">{f.explanation}</div>
                                                              {f.suggested_replacement && (
                                                                <button
                                                                  onClick={() => applyV2Finding(openReview.review_id, f.finding_id, active.draft_id, c.concept_id)}
                                                                  className="text-[9px] mt-0.5 px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                                                                >Appliquer suggestion</button>
                                                              )}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      )}

                                                      {/* Proposed revision globale */}
                                                      {openReview.proposed_revision && (
                                                        <div className={`rounded p-1.5 ${
                                                          openReview.proposed_revision.valid
                                                            ? 'bg-blue-600/10 border border-blue-600/40'
                                                            : 'bg-red-500/10 border border-red-500/40'
                                                        }`} data-testid={`mkt-review-proposed-${idx}`}>
                                                          <div className={`uppercase font-bold text-[9px] mb-1 ${
                                                            openReview.proposed_revision.valid ? 'text-blue-300' : 'text-red-300'
                                                          }`}>
                                                            Révision proposée{' '}
                                                            {openReview.proposed_revision.valid ? '(valide)' : '(invalide)'}
                                                          </div>
                                                          {openReview.proposed_revision.rationale && (
                                                            <div className="text-[9px] text-slate-400 italic mb-1">
                                                              {openReview.proposed_revision.rationale}
                                                            </div>
                                                          )}
                                                          {(openReview.proposed_revision.patches || []).slice(0, 5).map((p, i) => (
                                                            <div key={i} className="text-[10px] mb-1">
                                                              <div className="text-slate-400 text-[9px]">{p.field}</div>
                                                              <div className="text-slate-500 line-through">{p.before}</div>
                                                              <div className="text-emerald-300">→ {p.after}</div>
                                                            </div>
                                                          ))}
                                                          {!openReview.proposed_revision.valid && (
                                                            <div className="text-[9px] text-red-300 mt-1">
                                                              {(openReview.proposed_revision.validation_errors || []).slice(0, 3).map((e, i) => (
                                                                <div key={i}>✗ {e}</div>
                                                              ))}
                                                            </div>
                                                          )}
                                                          {openReview.proposed_revision.valid && openReview.status === 'open' && (
                                                            <button
                                                              onClick={() => applyV2Revision(openReview.review_id, active.draft_id, c.concept_id)}
                                                              disabled={v2ReviewActioning === openReview.review_id}
                                                              className="text-[10px] mt-1 px-2 py-0.5 rounded border border-blue-600/40 bg-blue-600/20 text-blue-100 hover:bg-blue-600/30 disabled:opacity-50"
                                                              data-testid={`mkt-review-apply-revision-${idx}`}
                                                            >Appliquer la révision complète</button>
                                                          )}
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                            {/* Actions */}
                                            {['pending_review', 'draft'].includes(active.status) && (
                                              <div className="flex items-center gap-2 pt-1 border-t border-slate-700 flex-wrap">
                                                <button
                                                  onClick={() => approveV2Draft(active.draft_id, c.concept_id)}
                                                  disabled={v2DraftActioning === active.draft_id}
                                                  className="text-[10px] px-2 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50"
                                                  data-testid={`mkt-draft-approve-${idx}`}
                                                >✓ Approuver</button>
                                                <button
                                                  onClick={() => rejectV2Draft(active.draft_id, c.concept_id)}
                                                  disabled={v2DraftActioning === active.draft_id}
                                                  className="text-[10px] px-2 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 disabled:opacity-50"
                                                  data-testid={`mkt-draft-reject-${idx}`}
                                                >✗ Rejeter</button>
                                                <button
                                                  onClick={() => regenerateV2Draft(active.draft_id, c.concept_id)}
                                                  disabled={v2DraftActioning === active.draft_id}
                                                  className="text-[10px] px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
                                                  data-testid={`mkt-draft-regenerate-${idx}`}
                                                >↻ Régénérer</button>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              ))}
                            </div>

                            {/* Source distribution */}
                            {v2Plan.source_distribution && Object.keys(v2Plan.source_distribution).length > 0 && (
                              <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                                <div className="text-[9px] uppercase tracking-wider text-slate-400 mb-2">Répartition effective des sources</div>
                                <div className="flex flex-wrap gap-1.5 text-[10px]">
                                  {Object.entries(v2Plan.source_distribution).map(([k, v]) => (
                                    <span key={k} className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-slate-300">
                                      {k}: <strong className="text-amber-300">{v}%</strong>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Opportunity gaps (globaux) */}
                            {(v2Plan.opportunity_gaps || []).length > 0 && (
                              <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3 text-[11px]"
                                   data-testid="mkt-planner-gaps">
                                <div className="text-[9px] uppercase tracking-wider text-amber-300 mb-1.5">Données manquantes ({v2Plan.opportunity_gaps.length})</div>
                                <ul className="space-y-1 text-slate-200">
                                  {v2Plan.opportunity_gaps.map((g, i) => (
                                    <li key={i} className="flex items-start gap-1.5">
                                      <span className="text-amber-300 mt-0.5">•</span>
                                      <span>
                                        <strong>{g.affected_source_type}</strong> — {g.description}
                                        {g.suggested_action && (
                                          <span className="block text-[10px] text-slate-400 mt-0.5">
                                            Action : {g.suggested_action}
                                          </span>
                                        )}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Warnings */}
                            {(v2Plan.warnings || []).length > 0 && (
                              <details className="bg-slate-800/30 border border-slate-700 rounded-lg p-2 text-[10px] text-slate-400">
                                <summary className="cursor-pointer">Warnings techniques ({v2Plan.warnings.length})</summary>
                                <ul className="mt-1.5 space-y-0.5 list-disc list-inside">
                                  {v2Plan.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rappel politique éditoriale */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
            <ShieldCheck className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 leading-relaxed">
              <strong>Règle éditoriale stricte</strong> — Aucune information réglementaire, fiscale,
              juridique ou aide (MaPrimeRénov', CEE, Fonds Vert, DPE…) ne doit être publiée sur la
              seule base d'un média. L'agent signale automatiquement les sujets « à vérifier auprès
              d'une source officielle » : contrôle-les avant publication.
            </div>
          </div>

          {/* Agent autonome — banner Sunday validation */}
          <div className={`rounded-xl border-2 p-4 flex items-center justify-between flex-wrap gap-3 ${
            pendingCount > 0
              ? 'bg-gradient-to-r from-emerald-50 to-blue-50 border-emerald-300'
              : 'bg-slate-50 border-slate-200'
          }`} data-testid="mkt-agent-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-700 to-emerald-500 flex items-center justify-center text-white text-lg">
                🤖
              </div>
              <div>
                {pendingCount > 0 ? (
                  <>
                    <div className="font-bold text-slate-900">
                      {pendingCount} post{pendingCount > 1 ? 's' : ''} en attente de validation
                    </div>
                    <div className="text-xs text-slate-600">
                      L'agent a préparé le programme de la semaine — textes, visuels, planning. Valide en 1 clic pour tout programmer.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-bold text-slate-900">Agent Marketing Autonome</div>
                    <div className="text-xs text-slate-600">
                      Chaque dimanche à 7h, l'agent génère automatiquement 5 posts (textes + visuels + planning) pour la semaine. Tu n'as qu'à valider.
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {pendingCount > 0 && (
                <>
                  <Button
                    onClick={validateWeek}
                    disabled={validating}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="mkt-agent-validate"
                  >
                    {validating
                      ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Validation…</>
                      : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Valider les {pendingCount} posts</>}
                  </Button>
                  <Button
                    onClick={rebrandPending}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="mkt-agent-rebrand"
                    title="Applique la signature Reno Resolve (logo + barre or) sur les visuels des posts en attente"
                  >
                    <ImageIcon className="w-4 h-4 mr-1.5" /> Re-brander visuels
                  </Button>
                </>
              )}
              <Button
                onClick={runAgentNow}
                disabled={agentRunning}
                variant={pendingCount > 0 ? "outline" : "default"}
                className={pendingCount === 0 ? "bg-blue-700 hover:bg-blue-800 text-white" : ""}
                data-testid="mkt-agent-run"
              >
                {agentRunning
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Génération…</>
                  : <><Zap className="w-4 h-4 mr-1.5" /> {pendingCount > 0 ? 'Regénérer' : 'Lancer maintenant'}</>}
              </Button>
            </div>
          </div>

          {/* Liste des posts pending (revue individuelle) */}
          {pendingPosts.length > 0 && (
            <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="mkt-agent-pending-list">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Newspaper className="w-4 h-4 text-emerald-600" />
                  Revue individuelle — valide ou rejette chaque post
                </h3>
                <span className="text-[11px] text-slate-500">{pendingPosts.length} post{pendingPosts.length > 1 ? 's' : ''}</span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingPosts.map(p => {
                  const preview = (p.variants?.linkedin || p.variants?.facebook || p.variants?.instagram || '').slice(0, 200);
                  return (
                    <div key={p.post_id} className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex flex-col hover:border-blue-500 hover:shadow-sm transition"
                         data-testid={`mkt-pending-${p.post_id}`}>
                      <button type="button" onClick={() => setPreviewPost(p)}
                              className="flex-1 text-left cursor-pointer"
                              data-testid={`mkt-pending-open-${p.post_id}`}>
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.theme}
                               className="w-full aspect-video object-cover rounded-lg mb-2" />
                        ) : (
                          <div className="w-full aspect-video rounded-lg mb-2 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-500 text-[11px]">
                            <div className="text-center px-2">
                              <ImageIcon className="w-6 h-6 mx-auto mb-1 opacity-50" />
                              Visuel non généré<br/>
                              <span className="italic text-[10px]">(Lovart crédits ?)</span>
                            </div>
                          </div>
                        )}
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">
                          {p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Non programmé'}
                        </div>
                        <div className="font-semibold text-sm text-slate-900 leading-tight mb-1 line-clamp-2">
                          {p.theme}
                        </div>
                        {preview && (
                          <p className="text-[11px] text-slate-600 leading-relaxed line-clamp-4">{preview}…</p>
                        )}
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          {p.variants?.linkedin && <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#0A66C2]/10 text-[#0A66C2]">LinkedIn</span>}
                          {p.variants?.facebook && <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#1877F2]/10 text-[#1877F2]">Facebook</span>}
                          {p.variants?.instagram && <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-pink-600/10 text-pink-600">Instagram</span>}
                          <span className="text-[9px] text-slate-400 ml-auto italic">Cliquer pour aperçu par plateforme</span>
                        </div>
                      </button>
                      <div className="flex gap-1.5 mt-3 pt-2 border-t border-slate-200">
                        <Button size="sm" variant="outline" onClick={() => rejectOne(p.post_id, p.theme)}
                                className="flex-1 text-red-600 hover:bg-red-50 border-red-200"
                                data-testid={`mkt-pending-reject-${p.post_id}`}>
                          <X className="w-3.5 h-3.5 mr-1" /> Rejeter
                        </Button>
                        <Button size="sm" onClick={() => validateOne(p.post_id)}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`mkt-pending-validate-${p.post_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Valider
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Statut LinkedIn */}
          <div className={`rounded-xl border p-4 flex items-center justify-between flex-wrap gap-3 ${
            linkedinStatus?.connected ? 'bg-sky-50 border-sky-200' :
            linkedinStatus?.configured ? 'bg-slate-50 border-slate-200' :
            'bg-amber-50 border-amber-200'
          }`} data-testid="linkedin-status-card">
            <div className="flex items-center gap-3">
              <Linkedin className="w-6 h-6 text-[#0a66c2]" />
              <div>
                {!linkedinStatus?.configured ? (
                  <>
                    <div className="font-semibold text-slate-800 text-sm">LinkedIn non configuré</div>
                    <div className="text-xs text-slate-600">
                      Ajoute <code>LINKEDIN_CLIENT_ID</code> et <code>LINKEDIN_CLIENT_SECRET</code> dans backend/.env pour activer la publication en 1 clic.
                    </div>
                  </>
                ) : linkedinStatus?.connected ? (
                  <>
                    <div className="font-semibold text-slate-800 text-sm">
                      LinkedIn connecté — {linkedinStatus.display_name || linkedinStatus.email || linkedinStatus.preferred_org_name || '✓'}
                    </div>
                    <div className="text-xs text-slate-600">
                      {linkedinStatus.preferred_org_name ? (
                        <>
                          <span className="font-semibold text-emerald-700">
                            Page : {linkedinStatus.preferred_org_name}
                          </span>
                          {' · '}Rôle : ADMINISTRATOR
                        </>
                      ) : (
                        <span className="font-semibold text-amber-700">
                          ⚠ Aucune Page sélectionnée — choisis-en une dans le dropdown ci-contre
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Scopes : {linkedinStatus.scope}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-slate-800 text-sm">LinkedIn prêt à connecter</div>
                    <div className="text-xs text-slate-600">
                      Clique pour autoriser Reno Resolve à publier sur ta page.
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {linkedinStatus?.connected && linkedinOrgs.length > 0 && (
                <select value={selectedOrgUrn}
                  onChange={e => savePreferredOrg(e.target.value)}
                  className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white"
                  data-testid="linkedin-org-select">
                  <option value="" disabled>— Sélectionner une Page LinkedIn —</option>
                  {linkedinOrgs.map(o => (
                    <option key={o.urn} value={o.urn}>{o.name} ({o.role})</option>
                  ))}
                </select>
              )}
              {linkedinStatus?.configured && !linkedinStatus.connected && (
                <Button onClick={connectLinkedin}
                  className="bg-[#0a66c2] hover:bg-[#004182] text-white text-sm"
                  data-testid="linkedin-connect">
                  <Linkedin className="w-4 h-4 mr-1.5" /> Connecter LinkedIn
                </Button>
              )}
              {linkedinStatus?.connected && (
                <Button variant="outline" onClick={disconnectLinkedin}
                  className="text-slate-600 text-sm"
                  data-testid="linkedin-disconnect">
                  Déconnecter
                </Button>
              )}
            </div>
          </div>

          {/* Statut Meta (Facebook + Instagram) */}
          <div className={`rounded-xl border p-4 flex items-center justify-between flex-wrap gap-3 ${
            metaStatus?.connected ? 'bg-blue-50 border-blue-200' :
            metaStatus?.configured ? 'bg-slate-50 border-slate-200' :
            'bg-amber-50 border-amber-200'
          }`} data-testid="meta-status-card">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-1">
                <Facebook className="w-6 h-6 text-[#1877F2]" />
                <Instagram className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                {!metaStatus?.configured ? (
                  <>
                    <div className="font-semibold text-slate-800 text-sm">Facebook & Instagram non configurés</div>
                    <div className="text-xs text-slate-600">
                      Ajoute <code>META_APP_ID</code> et <code>META_APP_SECRET</code> dans backend/.env pour activer la publication en 1 clic.
                    </div>
                  </>
                ) : metaStatus?.connected ? (
                  <>
                    <div className="font-semibold text-slate-800 text-sm">
                      Meta connecté — {metaStatus.facebook_name}
                    </div>
                    <div className="text-xs text-slate-600">
                      {metaStatus.can_publish_facebook ? '✓ Facebook' : '✗ Facebook'}
                      {' · '}
                      {metaStatus.can_publish_instagram ? '✓ Instagram Business' : '✗ Instagram (aucun compte Business relié)'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-slate-800 text-sm">Facebook & Instagram prêts à connecter</div>
                    <div className="text-xs text-slate-600">
                      Autorise Reno Resolve à publier sur ta page FB et ton compte Instagram Business.
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {metaStatus?.connected && metaStatus.pages?.length > 1 && (
                <select value={metaStatus.selected_page_id || ''}
                  onChange={e => selectMetaPage(e.target.value)}
                  className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white"
                  data-testid="meta-page-select">
                  {metaStatus.pages.map(p => (
                    <option key={p.page_id} value={p.page_id}>
                      {p.name}{p.ig_username ? ` · IG @${p.ig_username}` : ' · sans IG'}
                    </option>
                  ))}
                </select>
              )}
              {metaStatus?.configured && !metaStatus.connected && (
                <Button onClick={connectMeta}
                  className="bg-[#1877F2] hover:bg-[#125bd0] text-white text-sm"
                  data-testid="meta-connect">
                  <Facebook className="w-4 h-4 mr-1.5" /> Connecter Facebook + Instagram
                </Button>
              )}
              {metaStatus?.connected && (
                <Button variant="outline" onClick={disconnectMeta}
                  className="text-slate-600 text-sm"
                  data-testid="meta-disconnect">
                  Déconnecter
                </Button>
              )}
            </div>
          </div>

          {/* Sujets suggérés */}
          {suggestedTopics.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-bold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" /> Sujets tendance du jour
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {suggestedTopics.map(t => (
                  <div key={t.topic_id}
                    className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`mkt-topic-${t.topic_id}`}>
                    <div className="flex items-center gap-2 flex-wrap text-[10px] font-semibold uppercase tracking-wider mb-2">
                      <span className="bg-[#1C49A6] text-white px-2 py-0.5 rounded">
                        {t.source?.category_label}
                      </span>
                      {t.content_type && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                          {CONTENT_TYPE_LABELS[t.content_type] || t.content_type}
                        </span>
                      )}
                      {t.objective && (
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                          🎯 {OBJECTIVE_LABELS[t.objective] || t.objective}
                        </span>
                      )}
                      {t.source?.official && (
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" /> Source officielle
                        </span>
                      )}
                      {t.needs_verification && (
                        <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> À vérifier
                        </span>
                      )}
                    </div>
                    <h4 className="font-bold text-slate-900 leading-snug mb-2">{t.angle}</h4>
                    <p className="text-xs text-slate-600 mb-3">{t.why}</p>
                    {t.content_hook && (
                      <div className="text-xs bg-slate-50 border-l-2 border-[#1C49A6] p-2 rounded mb-3 italic text-slate-700">
                        « {t.content_hook} »
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mb-3 flex items-center gap-1">
                      <Newspaper className="w-3 h-3" /> {t.source?.name}
                      {t.source?.url && (
                        <a href={t.source.url} target="_blank" rel="noreferrer"
                          className="ml-auto text-[#1C49A6] hover:underline flex items-center gap-0.5">
                          Source <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    <Button onClick={() => applyTopicToComposer(t)}
                      className="mt-auto bg-[#1C49A6] hover:bg-[#163C89] text-white text-sm"
                      data-testid={`mkt-use-topic-${t.topic_id}`}>
                      Utiliser ce sujet <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aperçu du scan */}
          {scanSummary && (
            <details className="bg-white rounded-2xl border border-slate-200 p-4">
              <summary className="cursor-pointer font-semibold text-slate-800 flex items-center gap-2">
                <Radar className="w-4 h-4 text-slate-500" />
                Dernier scan — {scanSummary.total} articles (
                {new Date(scanSummary.scanned_at).toLocaleString('fr-FR')})
              </summary>
              <div className="mt-3 space-y-2">
                {scanSummary.categories.map(c => (
                  <div key={c.id} className="border-t border-slate-100 pt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-slate-800">{c.label}</span>
                      {c.official && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">Officielle</span>}
                      <span className="text-xs text-slate-400">({c.articles.length})</span>
                    </div>
                    <ul className="text-xs text-slate-600 space-y-1 pl-4">
                      {c.articles.slice(0, 3).map((a, i) => (
                        <li key={i} className="list-disc">
                          <a href={a.link} target="_blank" rel="noreferrer" className="hover:underline text-slate-700">
                            {a.source_name} — {a.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Historique */}
          {topicsHistory.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Newspaper className="w-4 h-4 text-slate-500" />
                Historique des sujets proposés ({topicsHistory.length})
              </h3>
              <div className="max-h-64 overflow-y-auto space-y-1 text-xs">
                {topicsHistory.map(t => (
                  <div key={t.topic_id} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
                    <span className={`w-2 h-2 rounded-full ${t.used ? 'bg-emerald-500' : 'bg-slate-300'}`} title={t.used ? 'Utilisé' : 'Non utilisé'}></span>
                    <span className="flex-1 text-slate-700 truncate">{t.angle}</span>
                    <span className="text-slate-400">{new Date(t.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {suggestedTopics.length === 0 && !loadingTopics && (
            <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-10 text-center text-slate-400">
              Aucune suggestion pour l'instant. Clique sur <strong className="text-slate-600">Proposer 3 sujets</strong> pour lancer l'agent.
            </div>
          )}
        </div>
      )}

      {view === 'compose' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Brief */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-blue-700" /> Brief éditorial
            </h2>
            <Field label="Thème / sujet du post *">
              <Input value={brief.theme}
                onChange={e => { setBrief({ ...brief, theme: e.target.value }); if (themeError) setThemeError(false); }}
                placeholder="ex : Isolation combles CEE — nouveau chantier école Louise Michel"
                data-testid="mkt-theme"
                className={themeError ? 'border-red-500 focus:border-red-500 ring-red-200' : ''} />
              {themeError && (
                <p className="text-[11px] text-red-600 mt-1 font-medium">
                  ⚠️ Champ obligatoire — précise un thème pour lancer la génération.
                </p>
              )}
            </Field>
            <Field label="Contexte / éléments concrets à mentionner">
              <Textarea rows={3} value={brief.context} onChange={e => setBrief({ ...brief, context: e.target.value })}
                placeholder="ex : 320m² isolés en 2 jours, économies 30%, subvention Fonds Vert obtenue…"
                data-testid="mkt-context" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cible principale">
                <Input value={brief.target_audience} onChange={e => setBrief({ ...brief, target_audience: e.target.value })}
                  placeholder="Mairies, artisans, particuliers…" />
              </Field>
              <Field label="Appel à l'action">
                <Input value={brief.call_to_action} onChange={e => setBrief({ ...brief, call_to_action: e.target.value })}
                  placeholder="Prendre RDV, demander devis…" />
              </Field>
            </div>
            <Field label="Plateformes">
              <div className="flex gap-2 flex-wrap">
                {PLATFORMS.map(p => {
                  const Icon = p.icon;
                  const on = brief.platforms.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => togglePlatform(p.id)}
                      className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium inline-flex items-center gap-1.5 transition-all ${on ? p.color : 'border-slate-200 text-slate-500 bg-white'}`}
                      data-testid={`mkt-plat-${p.id}`}>
                      <Icon className="w-4 h-4" /> {p.label}
                      {on && <CheckCircle2 className="w-3 h-3 ml-0.5" />}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Ton">
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'auto',   label: 'Auto (adapté par plateforme)' },
                  { id: 'expert', label: 'Expert & rassurant' },
                  { id: 'proche', label: 'Convivial & humain' },
                ].map(t => (
                  <button key={t.id} onClick={() => setBrief({ ...brief, tone: t.id })}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${brief.tone === t.id ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-600 bg-white'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Format du visuel">
              <div className="flex items-center gap-3 flex-wrap">
                <button type="button"
                  onClick={() => setBrief({ ...brief, carousel: false, carousel_template: null })}
                  className={`px-3 py-1.5 rounded-lg border-2 text-xs font-medium inline-flex items-center gap-1.5 transition-all ${
                    !brief.carousel ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-500 bg-white'
                  }`}
                  data-testid="mkt-format-single">
                  <ImageIcon className="w-3.5 h-3.5" /> Post simple
                </button>
                <button type="button"
                  onClick={() => setBrief({ ...brief, carousel: true })}
                  className={`px-3 py-1.5 rounded-lg border-2 text-xs font-medium inline-flex items-center gap-1.5 transition-all ${
                    brief.carousel ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-500 bg-white'
                  }`}
                  data-testid="mkt-format-carousel">
                  <Layers className="w-3.5 h-3.5" /> Carrousel
                </button>
              </div>

              {brief.carousel && (
                <div className="mt-3 space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    Gabarit (DA Reno Resolve intégrée)
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {/* Libre */}
                    <button type="button"
                      onClick={() => setBrief({ ...brief, carousel_template: null })}
                      className={`text-left p-2.5 rounded-lg border-2 transition-all ${
                        !brief.carousel_template ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="mkt-template-free">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                        <span>✨</span> Libre
                        <span className="ml-auto text-[10px] font-normal text-slate-500">
                          {brief.carousel_slides} slides
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-600 mt-0.5">
                        Trame narrative libre, tu choisis le nombre de slides.
                      </div>
                    </button>

                    {/* Templates prêts */}
                    {carouselTemplates.map(t => (
                      <button type="button" key={t.id}
                        onClick={() => setBrief({ ...brief, carousel_template: t.id, carousel_slides: t.slides })}
                        className={`text-left p-2.5 rounded-lg border-2 transition-all ${
                          brief.carousel_template === t.id ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        data-testid={`mkt-template-${t.id}`}>
                        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                          <span>{t.emoji}</span> {t.label}
                          <span className="ml-auto text-[10px] font-normal text-slate-500">
                            {t.slides} slides
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-600 mt-0.5 line-clamp-2">
                          {t.description}
                        </div>
                      </button>
                    ))}
                  </div>

                  {!brief.carousel_template && (
                    <div className="flex items-center gap-2 text-xs text-slate-600 pt-1">
                      <label>Nombre de slides :</label>
                      <select value={brief.carousel_slides}
                        onChange={e => setBrief({ ...brief, carousel_slides: parseInt(e.target.value) })}
                        className="border border-slate-200 rounded px-2 py-1 bg-white text-xs"
                        data-testid="mkt-carousel-slides">
                        {[3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  )}

                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Le gabarit sélectionné structure la narration + oriente les visuels Lovart selon la DA Reno Resolve (bleu marine, doré, atelier d'architecte).
                  </p>
                </div>
              )}
            </Field>
            <Button onClick={generateText} disabled={generatingText} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="mkt-generate-text">
              {generatingText ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Générer {brief.carousel
                ? `le carrousel${brief.carousel_template ? ` (${carouselTemplates.find(t => t.id === brief.carousel_template)?.label || brief.carousel_template})` : ` (${brief.carousel_slides} slides)`}`
                : 'les textes'}
            </Button>
          </div>

          {/* Image */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-pink-600" /> Visuel (Lovart.ai)
            </h2>
            <Field label="Description de l'image souhaitée">
              <Textarea rows={3} value={imagePrompt} onChange={e => setImagePrompt(e.target.value)}
                placeholder="ex : Toit d'école en cours d'isolation, ouvriers en équipement de sécurité, lumière matinale, hyperréaliste"
                data-testid="mkt-img-prompt" />
              <p className="text-[11px] text-slate-400 mt-1">Vide = utilise le thème du brief. L'IA enrichit auto avec le style Reno Resolve.</p>
            </Field>
            <Field label="Format">
              <select value={imageRatio} onChange={e => setImageRatio(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                {ASPECT_RATIOS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 cursor-pointer group select-none"
                   data-testid="mkt-apply-brand-toggle">
              <input type="checkbox"
                     checked={applyBrand}
                     onChange={e => setApplyBrand(e.target.checked)}
                     className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer" />
              <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                Signature Reno Resolve
              </span>
              <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                logo + barre or
              </span>
            </label>
            <Button onClick={generateImage} disabled={generatingImage} className="w-full bg-pink-600 hover:bg-pink-700 text-white" data-testid="mkt-generate-image">
              {generatingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
              Générer l'image via Lovart
            </Button>
            {imageUrl && (
              <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                <img src={imageUrl} alt="Généré" className="w-full h-auto max-h-96 object-contain" />
                <button onClick={() => setImageUrl('')} className="absolute top-2 right-2 bg-white/90 rounded-full p-1">
                  <X className="w-4 h-4 text-red-500" />
                </button>
              </div>
            )}
          </div>

          {/* Résultats — Carrousel */}
          {carouselSlides.length > 0 && (
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-blue-700" /> Carrousel — {carouselSlides.length} slides
                  </h2>
                  {carouselTemplateLabel && (
                    <p className="text-xs text-blue-800 font-medium mt-0.5">
                      Gabarit DA : {carouselTemplateLabel}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={carouselImageRatio} onChange={e => setCarouselImageRatio(e.target.value)}
                    disabled={generatingCarouselImages}
                    className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
                    data-testid="mkt-carousel-ratio">
                    <option value="1:1">Carré 1:1 (Instagram feed)</option>
                    <option value="4:5">Portrait 4:5 (Insta portrait / Story)</option>
                    <option value="16:9">Paysage 16:9 (LinkedIn / Facebook)</option>
                  </select>
                  <Button onClick={generateCarouselImages}
                    disabled={generatingCarouselImages}
                    className="bg-pink-600 hover:bg-pink-700 text-white text-sm"
                    data-testid="mkt-carousel-generate-images">
                    {generatingCarouselImages
                      ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Génération {carouselSlides.length} visuels…</>
                      : <><ImageIcon className="w-4 h-4 mr-1.5" /> Générer les {carouselSlides.length} visuels Lovart</>}
                  </Button>
                </div>
              </div>
              {generatingCarouselImages && (
                <p className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ⏳ Génération séquentielle en cours (~20s par slide). Ne ferme pas la page.
                </p>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {carouselSlides.map((slide, i) => (
                  <div key={i} className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-3 flex flex-col"
                       data-testid={`mkt-carousel-slide-${i + 1}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-white px-2 py-0.5 rounded-full">
                        Slide {i + 1}/{carouselSlides.length}
                      </span>
                      {slide.image_url && (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                          Visuel ✓
                        </span>
                      )}
                    </div>
                    {slide.image_url ? (
                      <div className="relative rounded-lg overflow-hidden mb-2 bg-slate-100 border border-blue-200"
                           style={{ aspectRatio: carouselImageRatio.replace(':', ' / ') }}>
                        <img src={slide.image_url} alt={`Slide ${i + 1}`}
                             className="w-full h-full object-cover"
                             data-testid={`mkt-carousel-slide-${i + 1}-image`} />
                      </div>
                    ) : (
                      <div className="rounded-lg mb-2 bg-gradient-to-br from-slate-100 to-slate-200 border border-dashed border-slate-300 flex items-center justify-center text-slate-400"
                           style={{ aspectRatio: carouselImageRatio.replace(':', ' / ') }}>
                        <div className="text-center px-3">
                          <ImageIcon className="w-6 h-6 mx-auto mb-1 opacity-40" />
                          <p className="text-[10px]">
                            {slide.image_error ? `❌ ${slide.image_error}` : 'Visuel à générer'}
                          </p>
                        </div>
                      </div>
                    )}
                    <h3 className="font-bold text-sm text-slate-900 mb-1 leading-tight">{slide.title}</h3>
                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap flex-1">{slide.body}</p>
                    <p className="text-[10px] text-slate-500 mt-2 italic border-t border-blue-200 pt-1.5 line-clamp-2">
                      🎨 {slide.image_prompt}
                    </p>
                  </div>
                ))}
              </div>
              {Object.keys(variants).length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-4">
                  <h3 className="font-bold text-sm text-slate-800 mb-2 flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4 text-emerald-600" /> Caption (accompagne le carrousel)
                  </h3>
                  <div className="grid md:grid-cols-3 gap-3">
                    {PLATFORMS.filter(p => variants[p.id]).map(p => {
                      const Icon = p.icon;
                      return (
                        <div key={p.id} className={`rounded-xl border p-3 ${p.color}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {p.label}</span>
                            <button onClick={() => copyText(p.id)} className="p-1 hover:bg-white/50 rounded">
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed">{variants[p.id]}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex justify-between items-end gap-2 flex-wrap bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex-1 min-w-[220px]">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Programmer pour (optionnel)
                  </label>
                  <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setVariants({}); setCarouselSlides([]); setCarouselTemplateLabel(''); setImageUrl(''); setScheduledAt(''); }}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Réinitialiser
                  </Button>
                  <Button onClick={savePost} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
                      (scheduledAt ? <Clock className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />)}
                    {scheduledAt ? 'Programmer' : 'Enregistrer le carrousel'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Résultats — Post simple */}
          {carouselSlides.length === 0 && Object.keys(variants).length > 0 && (
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-600" /> Aperçu des variantes
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {PLATFORMS.filter(p => variants[p.id]).map(p => {
                  const Icon = p.icon;
                  return (
                    <div key={p.id} className={`rounded-2xl border-2 p-4 ${p.color}`} data-testid={`mkt-variant-${p.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-bold flex items-center gap-1.5"><Icon className="w-4 h-4" /> {p.label}</span>
                        <button onClick={() => copyText(p.id)} className="p-1 hover:bg-white/50 rounded" data-testid={`mkt-copy-${p.id}`}>
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                      {imageUrl && <img src={imageUrl} alt="" className="w-full rounded-md mb-3 max-h-48 object-cover" />}
                      <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{variants[p.id]}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-end gap-2 flex-wrap bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex-1 min-w-[220px]">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Programmer pour (optionnel)
                  </label>
                  <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                    data-testid="mkt-schedule-input" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setVariants({}); setImageUrl(''); setScheduledAt(''); }}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Réinitialiser
                  </Button>
                  <Button onClick={savePost} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mkt-save">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
                      (scheduledAt ? <Clock className="w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />)}
                    {scheduledAt ? 'Programmer' : 'Enregistrer'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'calendar' && (
        <CalendarView
          calMonth={calMonth}
          setCalMonth={setCalMonth}
          calPosts={calPosts}
          loading={loadingCal}
          onOpenPost={openPost}
          onReschedule={async (postId, newDateISO) => {
            try {
              await api.patch(`/marketing/posts/${postId}/reschedule`, { scheduled_at: newDateISO });
              toast.success('Post reprogrammé');
              loadCalendar();
            } catch (e) {
              toast.error('Échec reprogrammation');
            }
          }}
        />
      )}

      {view === 'insights' && (
        <InsightsView
          summary={insightsSummary}
          analysis={insightsAnalysis}
          loading={loadingInsights}
          analyzing={analyzingInsights}
          onAnalyze={runInsightsAnalyze}
        />
      )}

      {showPlanModal && (
        <PlanWeekModal
          onClose={() => setShowPlanModal(false)}
          onSubmit={submitPlanWeek}
          submitting={planningWeek}
        />
      )}

      {previewPost && (
        <PostPreviewModal
          post={previewPost}
          onClose={() => setPreviewPost(null)}
          onRefresh={openPost}
          onDelete={async () => {
            if (!window.confirm('Supprimer ce post définitivement ?')) return;
            try {
              await api.delete(`/marketing/posts/${previewPost.post_id}`);
              toast.success('Post supprimé');
              setPreviewPost(null);
              if (view === 'library') loadPosts();
              if (view === 'calendar') loadCalendar();
            } catch (_) { toast.error('Échec suppression'); }
          }}
          onSavePerformance={(platform, values) => savePerformance(previewPost.post_id, platform, values)}
          linkedinAuthorUrn={selectedOrgUrn && selectedOrgUrn.startsWith('urn:li:organization:') ? selectedOrgUrn : ''}
        />
      )}

      {view === 'library' && (
        <div className="space-y-4">
          {posts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
              Aucun post enregistré. Compose ton premier post dans l'onglet Composer.
            </div>
          ) : posts.map(p => (
            <div key={p.post_id}
              onClick={() => openPost(p.post_id)}
              className="bg-white rounded-2xl border border-slate-200 p-4 cursor-pointer hover:shadow-md hover:border-slate-300 transition"
              data-testid={`mkt-post-${p.post_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-slate-900">{p.theme}</h3>
                  <p className="text-xs text-slate-500">
                    {new Date(p.created_at).toLocaleString('fr-FR')} · {Object.keys(p.variants || {}).length} plateforme(s)
                    {p.status && <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded text-slate-700">{p.status}</span>}
                    {p.content_type && <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded">{CONTENT_TYPE_LABELS[p.content_type] || p.content_type}</span>}
                  </p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deletePost(p.post_id); }} className="text-slate-300 hover:text-red-500 p-1" data-testid={`mkt-del-${p.post_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {p.image_url && <img src={p.image_url} alt="" className="w-32 rounded-md mb-2" />}
              <div className="grid md:grid-cols-3 gap-2 text-xs">
                {PLATFORMS.filter(pl => p.variants?.[pl.id]).map(pl => {
                  const Icon = pl.icon;
                  return (
                    <div key={pl.id} className={`rounded-lg border p-2 ${pl.color}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-[11px] flex items-center gap-1"><Icon className="w-3 h-3" />{pl.label}</span>
                        <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(p.variants[pl.id]); toast.success('Copié'); }}><Copy className="w-3 h-3" /></button>
                      </div>
                      <p className="line-clamp-4 text-slate-700">{p.variants[pl.id]}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

const MONTH_LABELS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function CalendarView({ calMonth, setCalMonth, calPosts, loading, onReschedule, onOpenPost }) {
  const { year, month } = calMonth;
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekday = (firstDay.getDay() + 6) % 7;

  const [dragOver, setDragOver] = useState(null);

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const postsByDay = {};
  calPosts.forEach(p => {
    if (!p.scheduled_at) return;
    const d = new Date(p.scheduled_at);
    if (d.getFullYear() === year && d.getMonth() + 1 === month) {
      const day = d.getDate();
      postsByDay[day] = postsByDay[day] || [];
      postsByDay[day].push(p);
    }
  });

  const goPrev = () => {
    let y = year, m = month - 1;
    if (m < 1) { m = 12; y -= 1; }
    setCalMonth({ year: y, month: m });
  };
  const goNext = () => {
    let y = year, m = month + 1;
    if (m > 12) { m = 1; y += 1; }
    setCalMonth({ year: y, month: m });
  };
  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === d;

  const handleDrop = (targetDay, ev) => {
    ev.preventDefault();
    setDragOver(null);
    const postId = ev.dataTransfer.getData('text/plain');
    if (!postId || !onReschedule) return;
    // Retrouve le post pour préserver l'heure d'origine si dispo
    const existing = calPosts.find(p => p.post_id === postId);
    let hour = 9, minute = 0;
    if (existing?.scheduled_at) {
      const d = new Date(existing.scheduled_at);
      hour = d.getHours(); minute = d.getMinutes();
    }
    const target = new Date(year, month - 1, targetDay, hour, minute, 0);
    // Format ISO local sans timezone shift
    const pad = (n) => String(n).padStart(2, '0');
    const iso = `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}:00`;
    onReschedule(postId, iso);
  };

  return (
    <div className="space-y-4" data-testid="mkt-calendar-view">
      <div className="bg-gradient-to-br from-[#1C49A6] to-[#0a4bab] rounded-2xl p-5 text-white flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-white/15 rounded-xl p-2.5"><CalendarIcon className="w-6 h-6" /></div>
          <div>
            <h2 className="text-lg font-bold">Planning éditorial</h2>
            <p className="text-xs text-blue-100">
              {calPosts.length} post(s) programmé(s) ce mois · glisse un post pour le reprogrammer
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg p-1">
          <button onClick={goPrev} className="p-2 hover:bg-white/20 rounded" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 font-semibold min-w-[150px] text-center">
            {MONTH_LABELS[month - 1]} {year}
          </span>
          <button onClick={goNext} className="p-2 hover:bg-white/20 rounded" data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {WEEKDAYS.map(w => (
                <div key={w} className="text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider py-2">
                  {w}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, idx) => {
                const dayPosts = d ? (postsByDay[d] || []) : [];
                const highlighted = dragOver === d;
                return (
                  <div key={idx}
                    onDragOver={(e) => { if (d) { e.preventDefault(); setDragOver(d); } }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => d && handleDrop(d, e)}
                    className={`min-h-[100px] rounded-md border p-1.5 transition-colors ${
                      d
                        ? (highlighted
                            ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-300'
                            : isToday(d)
                              ? 'border-[#1C49A6] bg-blue-50/40'
                              : 'border-slate-100 bg-white')
                        : 'border-transparent'
                    }`}
                    data-testid={d ? `cal-day-${d}` : undefined}>
                    {d && (
                      <>
                        <div className={`text-xs font-semibold ${isToday(d) ? 'text-[#1C49A6]' : 'text-slate-600'}`}>{d}</div>
                        <div className="space-y-1 mt-1">
                          {dayPosts.slice(0, 3).map(p => {
                            const platforms = Object.keys(p.variants || {});
                            const pDate = p.scheduled_at ? new Date(p.scheduled_at) : null;
                            const hh = pDate ? `${String(pDate.getHours()).padStart(2,'0')}:${String(pDate.getMinutes()).padStart(2,'0')}` : '';
                            const st = postCompletionStatus(p);
                            return (
                              <div key={p.post_id}
                                draggable
                                onDragStart={(e) => { e.dataTransfer.setData('text/plain', p.post_id); }}
                                onClick={() => onOpenPost && onOpenPost(p.post_id)}
                                className="text-[10px] bg-[#1C49A6] text-white rounded px-1.5 py-0.5 truncate cursor-grab active:cursor-grabbing hover:bg-[#163C89] flex items-center gap-1"
                                title={`${p.theme} — ${platforms.join(', ')} · ${st.label}`}
                                data-testid={`cal-post-${p.post_id}`}>
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${COMPLETION_DOT[st.color]}`}></span>
                                {hh && <span className="opacity-70">{hh}</span>}
                                <span className="truncate">{p.theme}</span>
                              </div>
                            );
                          })}
                          {dayPosts.length > 3 && (
                            <div className="text-[10px] text-slate-400">+{dayPosts.length - 3} autre(s)</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3 flex-wrap gap-2 text-[11px] text-slate-500">
              <span className="flex items-center gap-1">
                <ArrowRight className="w-3 h-3" /> Astuce : glisse un post sur une autre case pour le reprogrammer.
              </span>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> À préparer</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> Texte prêt</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Prêt à publier</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500"></span> Publié</span>
              </div>
            </div>
          </div>

          {calPosts.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" /> Programmation détaillée
              </h3>
              <div className="space-y-2">
                {calPosts.map(p => {
                  const d = new Date(p.scheduled_at);
                  const st = postCompletionStatus(p);
                  return (
                    <div key={p.post_id}
                      onClick={() => onOpenPost && onOpenPost(p.post_id)}
                      className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 text-sm cursor-pointer hover:bg-slate-50 rounded px-2 -mx-2"
                      data-testid={`cal-list-${p.post_id}`}>
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COMPLETION_DOT[st.color]}`} title={st.label}></span>
                      <div className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded font-mono">
                        {d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} · {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="flex-1 truncate text-slate-800">
                        {p.theme}
                        {p.content_type && (
                          <span className="ml-2 text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded uppercase font-semibold">
                            {CONTENT_TYPE_LABELS[p.content_type] || p.content_type}
                          </span>
                        )}
                        {p.objective && (
                          <span className="ml-1 text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            🎯 {OBJECTIVE_LABELS[p.objective] || p.objective}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {Object.keys(p.variants || {}).map(k => (
                          <span key={k} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 uppercase">{k[0]}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {calPosts.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-10 text-center text-slate-400">
              Aucun post programmé pour {MONTH_LABELS[month - 1]} {year}. Passe par le Composer et renseigne une date/heure pour planifier.
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Modal d'aperçu d'un post
// ---------------------------------------------------------------------------

function PostPreviewModal({ post, onClose, onDelete, onSavePerformance, onRefresh, linkedinAuthorUrn }) {
  const [activePlatform, setActivePlatform] = useState(
    Object.keys(post.variants || {})[0] || 'linkedin'
  );
  const [liveMockup, setLiveMockup] = useState(true);
  const [perfDraft, setPerfDraft] = useState({});
  const [genText, setGenText] = useState(false);
  const [genImages, setGenImages] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const handlePerfChange = (platform, field, value) => {
    setPerfDraft(prev => ({
      ...prev,
      [platform]: { ...(prev[platform] || {}), [field]: value === '' ? null : Number(value) }
    }));
  };

  const submitPerformance = (platform) => {
    const values = perfDraft[platform] || {};
    if (Object.values(values).every(v => v == null || Number.isNaN(v))) {
      toast.error('Renseigne au moins une valeur'); return;
    }
    onSavePerformance(platform, values);
  };

  const platformLabel = { linkedin: 'LinkedIn', facebook: 'Facebook', instagram: 'Instagram', instagram_story: 'IG Story' };
  const platformIcon  = { linkedin: Linkedin, facebook: Facebook, instagram: Instagram, instagram_story: Instagram };
  const platformAspect= { linkedin: '16:9', facebook: '1:1', instagram: '1:1', instagram_story: '4:5' };
  const hasVariants = Object.keys(post.variants || {}).length > 0;
  const availablePlatforms = hasVariants
    ? Object.keys(post.variants)
    : ['linkedin', 'facebook', 'instagram'];
  const currentText = post.variants?.[activePlatform] || '';
  const currentPerf = post.performance?.[activePlatform] || {};
  const currentImage = (post.images && post.images[activePlatform]) || post.image_url || null;
  const completion = postCompletionStatus(post);
  const canPublish = completion.color === 'green' && post.status !== 'published';

  const triggerGenerateText = async () => {
    setGenText(true);
    try {
      await api.post(`/marketing/posts/${post.post_id}/generate-content`);
      toast.success('Textes générés');
      onRefresh && onRefresh(post.post_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec génération');
    }
    setGenText(false);
  };

  const triggerGenerateImages = async () => {
    setGenImages(true);
    try {
      const r = await api.post(`/marketing/posts/${post.post_id}/generate-images`);
      toast.info(`Génération lancée (~${r.data.expected_seconds}s pour ${r.data.aspects.length} formats)`);
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const rr = await api.get(`/marketing/posts/${post.post_id}`);
          if (rr.data.images_status === 'done' || attempts >= 20) {
            setGenImages(false);
            onRefresh && onRefresh(post.post_id);
            if (rr.data.images_status === 'done') toast.success('Images générées');
            return;
          }
          onRefresh && onRefresh(post.post_id);
          setTimeout(poll, 15000);
        } catch (_) {
          setGenImages(false);
        }
      };
      setTimeout(poll, 15000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec Lovart');
      setGenImages(false);
    }
  };

  const triggerPublish = async () => {
    if (!window.confirm(`Publier ce post sur ${availablePlatforms.join(', ')} ?`)) return;
    setPublishing(true);
    try {
      const r = await api.post(`/marketing/posts/${post.post_id}/publish`,
        { platforms: availablePlatforms, linkedin_author_urn: linkedinAuthorUrn || undefined });
      if (r.data.status === 'not_configured') {
        toast.warning(r.data.message, { duration: 8000 });
      } else if (r.data.status === 'published') {
        toast.success(`Publié sur ${r.data.published_to.join(', ')}`);
      } else {
        toast.info(`Partiel : ${r.data.published_to?.join(', ') || 'aucune'}`);
      }
      // Détail par plateforme
      Object.entries(r.data.results || {}).forEach(([plat, res]) => {
        if (res.status === 'error') toast.error(`${plat} : ${res.message}`, { duration: 6000 });
      });
      onRefresh && onRefresh(post.post_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec publication');
    }
    setPublishing(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="post-preview-modal">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-4 bg-gradient-to-r from-[#1C49A6] to-[#0a4bab] text-white">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1 text-[10px] uppercase tracking-wider font-semibold">
              <span className={`px-2 py-0.5 rounded flex items-center gap-1 ${
                completion.color === 'red' ? 'bg-red-400/30' :
                completion.color === 'orange' ? 'bg-amber-400/30' :
                completion.color === 'green' ? 'bg-emerald-400/30' :
                completion.color === 'blue' ? 'bg-sky-400/30' : 'bg-white/20'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${COMPLETION_DOT[completion.color]}`}></span>
                {completion.label}
              </span>
              {post.content_type && (
                <span className="bg-blue-300/30 px-2 py-0.5 rounded">
                  {CONTENT_TYPE_LABELS[post.content_type] || post.content_type}
                </span>
              )}
              {post.objective && (
                <span className="bg-amber-300/30 px-2 py-0.5 rounded">
                  🎯 {OBJECTIVE_LABELS[post.objective] || post.objective}
                </span>
              )}
              {post.scheduled_at && (
                <span className="bg-white/20 px-2 py-0.5 rounded flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(post.scheduled_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold truncate">{post.theme}</h2>
            {post.source_name && (
              <p className="text-xs text-blue-100 mt-1">
                Source : {post.source_name}
                {post.source_url && (
                  <a href={post.source_url} target="_blank" rel="noreferrer" className="ml-2 underline inline-flex items-center gap-1">
                    voir l'article <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded" data-testid="post-preview-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto grid md:grid-cols-[240px_1fr] gap-0">
          <div className="bg-slate-50 border-r border-slate-200 p-4 space-y-4">
            {currentImage ? (
              <div>
                <img src={currentImage} alt="" className="w-full rounded-lg border border-slate-200" />
                <div className="text-[10px] text-slate-400 mt-1 text-center">
                  Format {platformAspect[activePlatform] || '1:1'} — {platformLabel[activePlatform]}
                </div>
              </div>
            ) : (
              <div className="w-full aspect-square rounded-lg bg-slate-200 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                <ImageIcon className="w-8 h-8 opacity-40" />
                <span>Aucune image</span>
              </div>
            )}
            <Button onClick={triggerGenerateImages} disabled={genImages || post.images_status === 'generating'}
              className="w-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs"
              data-testid="preview-gen-images">
              {(genImages || post.images_status === 'generating')
                ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Génération en cours…</>
                : <><Wand2 className="w-3 h-3 mr-1.5" /> Générer les visuels (3 formats)</>}
            </Button>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Plateforme</div>
              <div className="flex flex-col gap-1">
                {availablePlatforms.map(pl => {
                  const Icon = platformIcon[pl] || Linkedin;
                  const active = pl === activePlatform;
                  return (
                    <button key={pl} onClick={() => setActivePlatform(pl)}
                      className={`text-left text-sm px-3 py-2 rounded-lg font-medium flex items-center gap-2 ${
                        active ? 'bg-[#1C49A6] text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                      }`}
                      data-testid={`preview-plat-${pl}`}>
                      <Icon className="w-4 h-4" />
                      {platformLabel[pl] || pl}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {!hasVariants ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 text-center">
                <Bot className="w-8 h-8 mx-auto text-[#1C49A6] mb-2" />
                <p className="font-semibold text-slate-800 mb-1">Aucun texte pour l'instant</p>
                <p className="text-xs text-slate-600 max-w-md mx-auto mb-4">
                  Ce post a été pré-planifié par l'agent. Clique pour générer les 3 variantes
                  LinkedIn / Facebook / Instagram adaptées au type de contenu et à la source.
                </p>
                {post.notes && (
                  <div className="bg-white border border-slate-200 rounded p-3 text-left text-xs text-slate-600 mb-4 whitespace-pre-wrap">
                    <strong>Notes de l'agent :</strong>{"\n"}{post.notes}
                  </div>
                )}
                <Button onClick={triggerGenerateText} disabled={genText}
                  className="bg-[#1C49A6] hover:bg-[#163C89] text-white"
                  data-testid="preview-gen-text">
                  {genText ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Générer les textes
                </Button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
                    <Eye className="w-4 h-4 text-slate-500" /> Texte prêt à publier
                  </h3>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => setLiveMockup(!liveMockup)}
                      className={`text-xs flex items-center gap-1 px-2 py-0.5 rounded transition ${
                        liveMockup ? 'bg-[#0A66C2] text-white' : 'text-slate-500 hover:text-[#1C49A6] border border-slate-200'
                      }`}
                      data-testid="preview-toggle-mockup">
                      <Eye className="w-3 h-3" /> {liveMockup ? 'Vue brute' : 'Aperçu feed'}
                    </button>
                    <button onClick={triggerGenerateText} disabled={genText}
                      className="text-xs text-slate-500 hover:text-[#1C49A6] flex items-center gap-1"
                      data-testid="preview-regen-text">
                      {genText ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Regénérer
                    </button>
                    <button onClick={() => { navigator.clipboard.writeText(currentText); toast.success('Texte copié'); }}
                      className="text-xs text-[#1C49A6] hover:underline flex items-center gap-1">
                      <Copy className="w-3 h-3" /> Copier
                    </button>
                  </div>
                </div>
                {liveMockup ? (
                  <PlatformMockup platform={activePlatform} text={currentText}
                                  imageUrl={currentImage} pageName="Reno Resolve" />
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto"
                       data-testid="preview-text-content">
                    {currentText}
                  </div>
                )}
                <div className="text-[11px] text-slate-400 mt-1">{currentText.length} caractères</div>
              </div>
            )}

            <div className="border-t border-slate-200 pt-4">
              <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5 mb-3">
                <BarChart3 className="w-4 h-4 text-emerald-600" /> Performances {platformLabel[activePlatform]}
                <span className="text-[10px] text-slate-400 font-normal">(saisis depuis LinkedIn Analytics / Meta Insights)</span>
              </h3>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  ['impressions', 'Impressions', Eye],
                  ['reach', 'Portée', Users],
                  ['likes', 'Likes', TrendingUp],
                  ['comments', 'Commentaires', MessageSquare],
                  ['shares', 'Partages', Share2],
                  ['clicks', 'Clics', MousePointer],
                  ['leads', 'Leads', Target],
                ].map(([field, label, Icon]) => (
                  <div key={field}>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                      <Icon className="w-3 h-3" /> {label}
                    </label>
                    <input type="number" min="0"
                      defaultValue={currentPerf[field] ?? ''}
                      onChange={(e) => handlePerfChange(activePlatform, field, e.target.value)}
                      className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-white"
                      data-testid={`perf-${activePlatform}-${field}`} />
                  </div>
                ))}
              </div>
              <Button onClick={() => submitPerformance(activePlatform)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                data-testid={`perf-save-${activePlatform}`}>
                <Save className="w-4 h-4 mr-1.5" /> Enregistrer les stats {platformLabel[activePlatform]}
              </Button>
              {currentPerf.measured_at && (
                <p className="text-[10px] text-slate-400 mt-2">
                  Dernière mise à jour : {new Date(currentPerf.measured_at).toLocaleString('fr-FR')}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-between bg-slate-50 flex-wrap gap-2">
          <div className="text-xs text-slate-500">
            Créé le {new Date(post.created_at).toLocaleString('fr-FR')}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={onDelete}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="post-preview-delete">
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
            </Button>
            {canPublish && (
              <Button onClick={triggerPublish} disabled={publishing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="post-preview-publish">
                {publishing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Publier sur {availablePlatforms.length} plateforme(s)
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>Fermer</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onglet Insights
// ---------------------------------------------------------------------------

function InsightsView({ summary, analysis, loading, analyzing, onAnalyze }) {
  const totalPosts = summary?.posts_with_perf || 0;
  const platformTotals = summary?.totals_by_platform || {};
  const topPosts = summary?.top_posts || [];
  const byType = summary?.by_content_type || {};

  return (
    <div className="space-y-6" data-testid="mkt-insights-view">
      <div className="bg-gradient-to-br from-[#1C49A6] to-[#0a4bab] rounded-2xl p-5 text-white flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-white/15 rounded-xl p-2.5"><BarChart3 className="w-6 h-6" /></div>
          <div>
            <h2 className="text-lg font-bold">Insights & analyse IA</h2>
            <p className="text-xs text-blue-100">
              {totalPosts > 0
                ? `${totalPosts} post(s) avec stats · 90 derniers jours`
                : "Aucune donnée de perf — renseigne les stats depuis un post pour lancer l'analyse"}
            </p>
          </div>
        </div>
        <Button onClick={onAnalyze} disabled={analyzing || totalPosts === 0}
          className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold"
          data-testid="insights-analyze">
          {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
          Analyser avec l'IA
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : (
        <>
          {analysis && analysis.posts_analyzed > 0 && (
            <div className="bg-white rounded-2xl border-2 border-amber-300 p-5 shadow-sm">
              <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-amber-500" /> Verdict de l'agent
              </h3>
              <p className="text-sm text-slate-700 leading-relaxed mb-4">{analysis.verdict}</p>
              {analysis.strengths?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">Points forts</div>
                  <ul className="text-sm text-slate-700 space-y-1">
                    {analysis.strengths.map((s, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />{s}</li>)}
                  </ul>
                </div>
              )}
              {analysis.weaknesses?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">À améliorer</div>
                  <ul className="text-sm text-slate-700 space-y-1">
                    {analysis.weaknesses.map((w, i) => <li key={i} className="flex gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />{w}</li>)}
                  </ul>
                </div>
              )}
              {analysis.recommendations?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[#1C49A6] uppercase tracking-wider mb-2">Recommandations actionnables</div>
                  <div className="space-y-2">
                    {analysis.recommendations.map((r, i) => (
                      <div key={i} className="bg-blue-50 border-l-4 border-[#1C49A6] rounded p-3 text-sm">
                        <div className="font-semibold text-[#1C49A6] mb-1">{r.action}</div>
                        <div className="text-xs text-slate-600 mb-1.5">💡 {r.why}</div>
                        <div className="flex gap-2 flex-wrap text-[10px]">
                          {r.content_type_priority && (
                            <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded uppercase font-semibold">
                              {CONTENT_TYPE_LABELS[r.content_type_priority] || r.content_type_priority}
                            </span>
                          )}
                          {r.platform_focus && (
                            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded uppercase font-semibold">
                              {r.platform_focus}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            {PLATFORMS.map(p => {
              const Icon = p.icon;
              const t = platformTotals[p.id] || { count: 0 };
              return (
                <div key={p.id} className="bg-white rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                      <Icon className="w-4 h-4" /> {p.label}
                    </span>
                    <span className="text-xs text-slate-400">{t.count} post(s)</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-lg font-bold text-slate-900">{t.impressions || 0}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Impr.</div></div>
                    <div><div className="text-lg font-bold text-slate-900">{(t.likes || 0) + (t.comments || 0) + (t.shares || 0)}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Interactions</div></div>
                    <div><div className="text-lg font-bold text-emerald-600">{t.leads || 0}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">Leads</div></div>
                  </div>
                </div>
              );
            })}
          </div>

          {Object.keys(byType).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-500" /> Engagement moyen par type de contenu
              </h3>
              <div className="space-y-2">
                {Object.entries(byType).sort((a,b) => (b[1].avg_engagement||0) - (a[1].avg_engagement||0)).map(([ct, agg]) => (
                  <div key={ct} className="flex items-center gap-3 text-sm">
                    <span className="w-40 truncate text-slate-700">{CONTENT_TYPE_LABELS[ct] || ct}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-[#1C49A6] to-amber-400 h-full"
                        style={{ width: `${Math.min((agg.avg_engagement || 0) * 5, 100)}%` }}></div>
                    </div>
                    <span className="text-xs text-slate-500 font-mono w-16 text-right">{agg.avg_engagement || 0} / 100</span>
                    <span className="text-xs text-slate-400 w-10 text-right">{agg.count}×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {topPosts.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" /> Top posts
              </h3>
              <div className="space-y-1 text-sm">
                {topPosts.slice(0, 10).map((p, i) => (
                  <div key={p.post_id} className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
                    <span className="text-xs font-mono text-slate-400 w-6">#{i + 1}</span>
                    <span className="flex-1 truncate text-slate-800">{p.theme}</span>
                    {p.content_type && (
                      <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded uppercase">
                        {CONTENT_TYPE_LABELS[p.content_type] || p.content_type}
                      </span>
                    )}
                    <span className="text-xs text-emerald-600 font-semibold w-12 text-right">{p.engagement_score}</span>
                    {p.leads > 0 && <span className="text-xs bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">🎯 {p.leads}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {totalPosts === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-12 text-center text-slate-500">
              <BarChart3 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="font-semibold text-slate-700 mb-1">Aucune donnée de performance</p>
              <p className="text-xs max-w-md mx-auto">
                Une fois un post publié, ouvre-le depuis le Calendrier ou la Bibliothèque et
                renseigne les stats natives (LinkedIn analytics, Meta Insights…). L'agent IA
                les analysera et te dira quel type de contenu performe le mieux.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal "Planifier ma semaine" — choix personnalisé des 5 slots
// ---------------------------------------------------------------------------

const DEFAULT_WEEK_SLOTS = [
  { day_offset: 0, hour: 9,  content_type: 'actualite_btp',         objective: 'notoriete',            platforms_priority: 'linkedin' },
  { day_offset: 1, hour: 12, content_type: 'conseil_travaux',       objective: 'credibilite',          platforms_priority: 'facebook' },
  { day_offset: 2, hour: 9,  content_type: 'avant_apres',           objective: 'preuve_sociale',       platforms_priority: 'instagram' },
  { day_offset: 3, hour: 12, content_type: 'realisations_chantier', objective: 'generation_prospects', platforms_priority: 'linkedin' },
  { day_offset: 4, hour: 9,  content_type: 'erreur_a_eviter',       objective: 'engagement',           platforms_priority: 'facebook' },
];
const DAY_NAMES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function nextMondayISO() {
  const d = new Date();
  const weekday = d.getDay(); // 0 = Sunday
  const jsMonday = weekday === 0 ? 1 : (8 - weekday);
  const target = new Date(d);
  target.setDate(d.getDate() + jsMonday);
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`;
}

function PlanWeekModal({ onClose, onSubmit, submitting }) {
  const [slots, setSlots] = useState(DEFAULT_WEEK_SLOTS.map(s => ({ ...s })));
  const [startDate, setStartDate] = useState(nextMondayISO());

  const updateSlot = (idx, field, value) => {
    setSlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const addSlot = () => {
    setSlots(prev => [...prev, { day_offset: 4, hour: 15, content_type: 'materiaux', objective: 'engagement', platforms_priority: 'linkedin' }]);
  };
  const removeSlot = (idx) => {
    setSlots(prev => prev.filter((_, i) => i !== idx));
  };
  const resetDefault = () => setSlots(DEFAULT_WEEK_SLOTS.map(s => ({ ...s })));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="plan-week-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-4 bg-gradient-to-r from-[#1C49A6] to-[#0a4bab] text-white">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" /> Planifier ma semaine éditoriale
            </h2>
            <p className="text-xs text-blue-100 mt-1">
              Ajuste les créneaux, l'agent choisira le meilleur sujet dans les flux BTP pour chaque slot
              (diversité éditoriale forcée).
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded" data-testid="plan-week-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Semaine du</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="border border-slate-200 rounded px-3 py-1.5 text-sm bg-white"
                data-testid="plan-week-start" />
            </label>
            <button onClick={resetDefault} className="text-xs text-slate-500 hover:text-[#1C49A6] flex items-center gap-1 mt-4">
              <RefreshCw className="w-3 h-3" /> Réinitialiser aux valeurs Reno Resolve
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 pr-2">#</th>
                  <th className="text-left py-2 pr-2">Jour</th>
                  <th className="text-left py-2 pr-2">Heure</th>
                  <th className="text-left py-2 pr-2">Type de contenu</th>
                  <th className="text-left py-2 pr-2">Objectif</th>
                  <th className="text-left py-2 pr-2">Plateforme prio.</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {slots.map((s, idx) => (
                  <tr key={idx} className="border-b border-slate-100" data-testid={`plan-slot-${idx}`}>
                    <td className="py-2 pr-2 text-slate-400 font-mono">{idx + 1}</td>
                    <td className="py-2 pr-2">
                      <select value={s.day_offset} onChange={e => updateSlot(idx, 'day_offset', Number(e.target.value))}
                        className="border border-slate-200 rounded px-2 py-1 text-sm bg-white">
                        {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <input type="number" min="6" max="20" value={s.hour}
                        onChange={e => updateSlot(idx, 'hour', Number(e.target.value))}
                        className="border border-slate-200 rounded px-2 py-1 text-sm w-16 bg-white" />
                    </td>
                    <td className="py-2 pr-2">
                      <select value={s.content_type} onChange={e => updateSlot(idx, 'content_type', e.target.value)}
                        className="border border-slate-200 rounded px-2 py-1 text-sm bg-white min-w-[160px]">
                        {Object.entries(CONTENT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <select value={s.objective} onChange={e => updateSlot(idx, 'objective', e.target.value)}
                        className="border border-slate-200 rounded px-2 py-1 text-sm bg-white min-w-[140px]">
                        {Object.entries(OBJECTIVE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <select value={s.platforms_priority} onChange={e => updateSlot(idx, 'platforms_priority', e.target.value)}
                        className="border border-slate-200 rounded px-2 py-1 text-sm bg-white">
                        <option value="linkedin">LinkedIn</option>
                        <option value="facebook">Facebook</option>
                        <option value="instagram">Instagram</option>
                      </select>
                    </td>
                    <td className="py-2">
                      <button onClick={() => removeSlot(idx)}
                        className="text-slate-300 hover:text-red-500 p-1"
                        data-testid={`plan-remove-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button onClick={addSlot}
            className="text-xs text-[#1C49A6] hover:underline flex items-center gap-1"
            data-testid="plan-add-slot">
            + Ajouter un créneau
          </button>

          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-xs text-slate-600">
            <strong>Aperçu :</strong> {slots.length} créneau(x) à planifier pour la semaine du{' '}
            <strong>{startDate ? new Date(startDate).toLocaleDateString('fr-FR') : 'lundi prochain'}</strong>.
            L'agent choisira automatiquement le meilleur article dans les flux BTP pour chaque slot
            (diversité éditoriale + citation source vérifiée).
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={() => onSubmit(slots, startDate)} disabled={submitting || slots.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="plan-week-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-2" />}
            Planifier {slots.length} post{slots.length > 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}



// ============================================================================
// PlatformMockup — aperçu réaliste feed LinkedIn / Facebook / Instagram
// ============================================================================
function PlatformMockup({ platform, text, imageUrl, pageName = 'Reno Resolve' }) {
  const initials = pageName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  if (platform === 'linkedin') {
    return (
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm max-h-[520px] overflow-y-auto" data-testid="mockup-linkedin">
        <div className="flex items-center gap-2 p-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-[#1C49A6] text-white flex items-center justify-center text-xs font-bold">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-slate-900">{pageName}</div>
            <div className="text-[11px] text-slate-500">Entreprise · Rénovation énergétique BTP</div>
            <div className="text-[11px] text-slate-500">À l'instant · 🌐</div>
          </div>
        </div>
        <div className="px-3 pb-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{text}</div>
        {imageUrl && <img src={imageUrl} alt="" className="w-full max-h-96 object-cover" />}
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500">
          <span>👍 ❤️ 💡 42</span><span>3 commentaires · 5 republications</span>
        </div>
        <div className="flex items-center justify-around border-t border-slate-100 py-1 text-slate-600 text-xs">
          <span>👍 J'aime</span><span>💬 Commenter</span><span>🔄 Republier</span><span>📤 Envoyer</span>
        </div>
      </div>
    );
  }

  if (platform === 'facebook') {
    return (
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm max-h-[520px] overflow-y-auto" data-testid="mockup-facebook">
        <div className="flex items-center gap-2 p-3">
          <div className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center text-xs font-bold">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-slate-900">{pageName}</div>
            <div className="text-[11px] text-slate-500">À l'instant · 🌐</div>
          </div>
        </div>
        <div className="px-3 pb-3 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{text}</div>
        {imageUrl && <img src={imageUrl} alt="" className="w-full max-h-96 object-cover" />}
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500">
          <span>👍❤️😮 128</span><span>18 commentaires · 12 partages</span>
        </div>
        <div className="flex items-center justify-around border-t border-slate-100 py-1 text-slate-600 text-xs">
          <span>👍 J'aime</span><span>💬 Commenter</span><span>📤 Partager</span>
        </div>
      </div>
    );
  }

  // Instagram
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm max-h-[520px] overflow-y-auto" data-testid="mockup-instagram">
      <div className="flex items-center gap-2 p-3 border-b border-slate-100">
        <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-500 p-0.5">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-slate-800">{initials}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-900">reno.resolve</div>
        </div>
        <span className="text-slate-400 text-lg">⋯</span>
      </div>
      {imageUrl ? (
        <img src={imageUrl} alt="" className="w-full aspect-square object-cover" />
      ) : (
        <div className="w-full aspect-square bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-400 text-xs">Aucune image</div>
      )}
      <div className="px-3 py-2 space-y-1">
        <div className="flex items-center gap-3 text-xl text-slate-800">
          <span>♡</span><span>💬</span><span>↗</span><span className="ml-auto">🔖</span>
        </div>
        <div className="text-xs text-slate-700 font-semibold">1 245 j'aime</div>
        <div className="text-xs text-slate-800 line-clamp-4">
          <span className="font-semibold mr-1">reno.resolve</span>
          <span className="whitespace-pre-wrap">{text}</span>
        </div>
        <div className="text-[10px] text-slate-400">Il y a quelques secondes</div>
      </div>
    </div>
  );
}
